"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import {
  ArrowDown01Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  ArrowUp01Icon,
  ArrowUpDownIcon,
  Calendar03Icon,
  Cancel01Icon,
  File01Icon,
  FilterIcon,
  GalleryThumbnailsIcon,
  GridViewIcon,
  LayoutThreeColumnIcon,
  LeftToRightListBulletIcon,
  Search01Icon,
  Tick02Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  createFileTreeIconResolver,
  getBuiltInSpriteSheet,
  prepareFileTreeInput,
  type FileTreeDropResult,
  type FileTreeSortComparator,
  type FileTreeSortEntry,
} from "@pierre/trees";
import { FileTree as PierreFileTree, useFileTree } from "@pierre/trees/react";

import { cn } from "@/utils/misc";
import { Button } from "@/ui/primitives/button";
// Morgan extension: right-click menus over items (see `renderItemMenu`).
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
} from "@/ui/composed/menus/context-menu";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import { Input } from "@/ui/primitives/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { Spinner } from "@/ui/primitives/spinner";
import { Tabs, TabsList, TabsTrigger } from "@/ui/composed/sections/tabs";

import { FileThumbnail } from "./file-thumbnail";
// Morgan extension: business-document sprite symbols + extension remap.
import {
  MORGAN_FILE_ICON_COLORS,
  MORGAN_FILE_ICON_EXTENSIONS,
  MORGAN_FILE_ICON_SPRITE_SHEET,
  MORGAN_FILE_ICON_SYMBOLS,
} from "./file-system-icons";
import { ScrollArea, ScrollAreaPrimitive } from "./scroll-area";

// Morgan's dialog has no separate `DialogPanel` part (the Extend dialog
// splits the scrollable panel out of the content); a plain div carrying the
// same className slot is equivalent here.
function DialogPanel({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={className} {...props} />;
}

export type FileSystemView = "icons" | "list" | "columns" | "gallery";

export type FileSystemFolderItem = {
  kind: "folder";
  /** Folder prefix, e.g. `"invoices/2026/"`. A trailing slash is added when missing. */
  path: string;
  name?: string;
  parentPath?: string;
  /** Set when children exist but are not in `items` yet; enables `loadChildren`. */
  hasChildren?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type FileSystemFileItem = {
  kind: "file";
  /** Display/canonical path, e.g. `"invoices/2026/jan.pdf"`. */
  path: string;
  /** Original object key (S3/R2). Defaults to `path`. */
  key?: string;
  name?: string;
  parentPath?: string;
  contentType?: string;
  size?: number;
  createdAt?: string;
  updatedAt?: string;
  etag?: string;
  /** Optional if already public/presigned. Otherwise resolved via `getFileUrl`. */
  url?: string;
  /** Externally generated thumbnail. The component never renders documents itself. */
  previewImageUrl?: string | null;
  /**
   * Externally generated page thumbnails (first entry is the cover). When a
   * file has more than one page, large thumbnails show a hover pager.
   */
  previewImageUrls?: string[] | null;
  /**
   * Total page count when it exceeds `previewImageUrls.length`; the pager
   * loads the remaining pages on demand via `loadPreviewImageUrl`.
   */
  previewPageCount?: number;
  /** Thumbnail aspect ratio (width / height). Defaults to a portrait page. */
  previewAspectRatio?: number;
  metadata?: Record<string, string>;
};

export type FileSystemItem = FileSystemFolderItem | FileSystemFileItem;

export type FileSystemLoadChildrenArgs = {
  path: string;
  cursor: string | null;
};

export type FileSystemLoadChildrenResult = {
  items: FileSystemItem[];
  nextCursor?: string | null;
};

export type FileSystemProps = {
  /** Flat manifest. Folders are optional; missing prefixes are inferred from file paths. */
  items: FileSystemItem[];
  className?: string;
  /** Label for the root folder. */
  title?: string;
  defaultView?: FileSystemView;
  view?: FileSystemView;
  onViewChange?: (view: FileSystemView) => void;
  /** Folder prefix to open initially, e.g. `"invoices/"`. */
  defaultPath?: string;
  // Morgan extension: observe the Finder's current folder prefix (`""` at
  // the root, otherwise a trailing-slash path like `"invoices/2026/"`).
  // Every navigation mechanism (folder open, breadcrumb, back/forward)
  // flows through the internal history state this reports from.
  onCurrentPathChange?: (path: string) => void;
  onSelectionChange?: (item: FileSystemItem | null) => void;
  /**
   * Called on file open (double-click), replacing the built-in behavior. By
   * default image files open in a preview dialog and other files open their
   * resolved URL in a new tab (the upstream PDF/DOCX/XLSX viewers are
   * stripped from this vendored copy).
   */
  onFileOpen?: (file: FileSystemFileItem, url: string | null) => void;
  /** Resolve a URL (e.g. presigned) for a file without one. */
  getFileUrl?: (file: FileSystemFileItem) => string | Promise<string>;
  /** Lazily fetch children for folders with `hasChildren` and no loaded entries. */
  loadChildren?: (
    args: FileSystemLoadChildrenArgs,
  ) => Promise<FileSystemLoadChildrenResult>;
  /** Custom preview node for files without `previewImageUrl`. */
  renderFilePreview?: (file: FileSystemFileItem) => React.ReactNode;
  /**
   * Lazily render a page thumbnail beyond the eagerly provided
   * `previewImageUrls` (the pager calls this as pages come into view).
   */
  loadPreviewImageUrl?: (
    file: FileSystemFileItem,
    pageIndex: number,
  ) => Promise<string | null>;
  // Morgan extension: extra list-view cells rendered after the name cell.
  // Supplying either of these switches the list view from the @pierre/trees
  // tree (whose rows live in a closed shadow DOM and cannot host React
  // cells) to a flat, React-owned row list for the current folder.
  renderItemColumns?: (item: FileSystemItem) => React.ReactNode;
  // Morgan extension: extra classes on unselected list-view rows (e.g. a
  // failed-document highlight). Ignored while the row is selected so the
  // primary selection treatment stays intact.
  getItemClassName?: (item: FileSystemItem) => string | undefined;
  // Morgan extension: header cells matching `renderItemColumns` widths,
  // rendered between the Name and Date Modified column headers.
  itemColumnsHeader?: React.ReactNode;
  // Morgan extension: extra detail rows shown in the columns view preview
  // pane (under the file name, above the built-in Information block) — e.g.
  // a document's category and processing status. Files only.
  renderPreviewDetails?: (file: FileSystemFileItem) => React.ReactNode;
  // Morgan extension: a live, full-size preview for the large preview
  // surfaces only (columns view preview pane + gallery center stage), used
  // in place of the type-icon thumbnail when it returns a node. Heavy
  // renderers (Word/Excel/text) belong here, never in per-tile thumbnails.
  // Returning null falls back to the standard thumbnail.
  renderLargePreview?: (file: FileSystemFileItem) => React.ReactNode;
  // Morgan extension: context-menu content for the clicked selection.
  // Right-clicking an unselected item selects it first (Finder behavior);
  // the callback then receives every selected item.
  renderItemMenu?: (items: FileSystemItem[]) => React.ReactNode;
  // Morgan extension: multi-select surface — fires with every selected item
  // whenever the selection set changes (in addition to `onSelectionChange`,
  // which keeps reporting the primary item).
  onSelectionItemsChange?: (items: FileSystemItem[]) => void;
  // Morgan extension: internal drag of the selection onto a folder. The
  // target is the folder's trailing-slash path ("" = library root, which
  // tree-background drops resolve to via the open folder's own path).
  onMoveItems?: (items: FileSystemItem[], targetFolderPath: string) => void;
  // Morgan extension: OS files dropped onto a folder row/tile.
  onExternalDropOnFolder?: (
    dataTransfer: DataTransfer,
    targetFolderPath: string,
  ) => void;
  // Morgan extension: disables drag sources, drop targets and context menus.
  readOnly?: boolean;
  // Morgan extension: extra toolbar content. `toolbarStart` renders in the
  // header's left cell after the nav arrows / folder title; `toolbarEnd`
  // renders at the right edge of the header's trailing cluster (after the
  // sort/filter/search controls).
  toolbarStart?: React.ReactNode;
  toolbarEnd?: React.ReactNode;
  // Morgan extension: suppress the built-in search field / file-type+date
  // filter menu when the consumer supplies its own equivalents via
  // `toolbarEnd` (two searches or two filters in one bar would conflict).
  hideSearch?: boolean;
  hideFilter?: boolean;
};

type FolderEntry = FileSystemFolderItem & {
  name: string;
  parentPath: string;
};

type FileEntry = FileSystemFileItem & {
  key: string;
  name: string;
  parentPath: string;
};

type FileSystemEntry = FolderEntry | FileEntry;

type FileSystemIndex = {
  children: Map<string, FileSystemEntry[]>;
  files: Map<string, FileEntry>;
  folders: Map<string, FolderEntry>;
};

function normalizeFolderPath(path: string) {
  if (!path || path === "/") return "";
  return path.endsWith("/") ? path : `${path}/`;
}

function pathName(path: string) {
  const trimmed = path.endsWith("/") ? path.slice(0, -1) : path;
  const separatorIndex = trimmed.lastIndexOf("/");
  return separatorIndex === -1 ? trimmed : trimmed.slice(separatorIndex + 1);
}

function pathParent(path: string) {
  const trimmed = path.endsWith("/") ? path.slice(0, -1) : path;
  const separatorIndex = trimmed.lastIndexOf("/");
  return separatorIndex === -1 ? "" : trimmed.slice(0, separatorIndex + 1);
}

function fileExtension(name: string) {
  const dotIndex = name.lastIndexOf(".");
  return dotIndex === -1 ? "" : name.slice(dotIndex + 1).toLowerCase();
}

const FILE_KIND_LABELS: Record<string, string> = {
  csv: "CSV Document",
  doc: "Word Document",
  docx: "Word Document",
  gif: "GIF Image",
  jpeg: "JPEG Image",
  jpg: "JPEG Image",
  md: "Markdown Document",
  pdf: "PDF Document",
  png: "PNG Image",
  ppt: "PowerPoint Presentation",
  pptx: "PowerPoint Presentation",
  svg: "SVG Image",
  tsv: "TSV Document",
  txt: "Plain Text",
  webp: "WebP Image",
  xls: "Excel Workbook",
  xlsx: "Excel Workbook",
  zip: "ZIP Archive",
};

function fileKindLabel(file: FileEntry) {
  const byExtension = FILE_KIND_LABELS[fileExtension(file.name)];

  if (byExtension) return byExtension;
  if (file.contentType?.startsWith("image/")) return "Image";

  return file.contentType ?? "Document";
}

// MIME types inferred from the extension when a file carries no
// `contentType`, so the file-type filter can classify every manifest entry.
const EXTENSION_MIME_TYPES: Record<string, string> = {
  css: "text/css",
  csv: "text/csv",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  gif: "image/gif",
  go: "text/x-go",
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  js: "text/javascript",
  json: "application/json",
  jsx: "text/jsx",
  md: "text/markdown",
  mdx: "text/mdx",
  pdf: "application/pdf",
  png: "image/png",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  py: "text/x-python",
  rs: "text/x-rust",
  sh: "application/x-sh",
  sql: "application/sql",
  svg: "image/svg+xml",
  ts: "text/x-typescript",
  tsv: "text/tab-separated-values",
  tsx: "text/x-typescript",
  txt: "text/plain",
  webp: "image/webp",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  yaml: "text/yaml",
  yml: "text/yaml",
  zip: "application/zip",
};

const FALLBACK_MIME_TYPE = "application/octet-stream";
const IPAD_MIN_WIDTH = 768;

// Morgan extension: dataTransfer type marking an internal item drag, so
// folder drop targets can distinguish it from OS file drags (whose `types`
// include "Files").
const MORGAN_ITEMS_DRAG_TYPE = "application/x-morgan-items";

const MIME_TYPE_LABELS: Record<string, string> = {
  [FALLBACK_MIME_TYPE]: "Binary",
  "application/json": "JSON",
  "application/msword": "Word document (legacy)",
  "application/pdf": "PDF",
  "application/sql": "SQL",
  "application/vnd.ms-excel": "Excel workbook (legacy)",
  "application/vnd.ms-powerpoint": "PowerPoint (legacy)",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "PowerPoint",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    "Excel workbook",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "Word document",
  "application/x-sh": "Shell script",
  "application/zip": "ZIP archive",
  "image/gif": "GIF image",
  "image/jpeg": "JPEG image",
  "image/png": "PNG image",
  "image/svg+xml": "SVG image",
  "image/webp": "WebP image",
  "text/css": "CSS",
  "text/csv": "CSV",
  "text/javascript": "JavaScript",
  "text/jsx": "JSX",
  "text/markdown": "Markdown",
  "text/mdx": "MDX",
  "text/plain": "Plain text",
  "text/tab-separated-values": "TSV",
  "text/x-go": "Go",
  "text/x-python": "Python",
  "text/x-rust": "Rust",
  "text/x-typescript": "TypeScript",
  "text/yaml": "YAML",
};

function mimeTypeForFile(file: FileEntry) {
  return (
    file.contentType ??
    EXTENSION_MIME_TYPES[fileExtension(file.name)] ??
    FALLBACK_MIME_TYPE
  );
}

export type FileSystemViewerKind = "docx" | "image" | "pdf" | "xlsx";

function viewerKindForFile(
  file: FileSystemFileItem,
): FileSystemViewerKind | null {
  // Morgan strips the built-in pdf/docx/xlsx viewers (Spec 2 supplies its
  // own document viewers and consumers override opens via `onFileOpen`);
  // only the dependency-free image preview remains. Without an
  // `onFileOpen` handler, non-image files open in a new tab.
  if (file.contentType?.startsWith("image/")) return "image";

  const name = (file.name ?? file.path).toLowerCase();

  if (/\.(avif|gif|jpe?g|png|svg|webp)$/.test(name)) return "image";

  return null;
}

// PDF and DOCX pages want height; spreadsheets want width; images get a
// roomy but contained frame.
const VIEWER_DIALOG_CLASSNAMES: Record<FileSystemViewerKind, string> = {
  docx: "h-[88vh] w-[min(96vw,68rem)] max-w-none",
  image: "max-h-[88vh] w-fit max-w-[min(96vw,64rem)]",
  pdf: "h-[88vh] w-[min(96vw,68rem)] max-w-none",
  xlsx: "h-[85vh] w-[min(96vw,100rem)] max-w-none",
};

function formatByteSize(size: number | undefined) {
  if (size === undefined) return null;
  if (size < 1000) return `${size} bytes`;

  const units = ["KB", "MB", "GB", "TB"];
  let value = size;

  for (const unit of units) {
    value /= 1000;
    if (value < 1000 || unit === "TB") {
      return `${value >= 100 ? Math.round(value) : value.toFixed(value >= 10 ? 1 : 2).replace(/\.?0+$/, "")} ${unit}`;
    }
  }

  return null;
}

function formatTimestamp(value: string | undefined) {
  if (!value) return null;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  const day = date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

  return `${day} at ${time}`;
}

// Every directory prefix appearing in the given relative file paths.
function directoryPathsOf(paths: readonly string[]) {
  const directoryPaths = new Set<string>();

  for (const relativePath of paths) {
    let slashIndex = relativePath.indexOf("/");

    while (slashIndex !== -1) {
      directoryPaths.add(relativePath.slice(0, slashIndex));
      slashIndex = relativePath.indexOf("/", slashIndex + 1);
    }
  }
  return directoryPaths;
}

function compareEntryNames(left: { name: string }, right: { name: string }) {
  return left.name.localeCompare(right.name, undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

export type FileSystemSortKey = "createdAt" | "name" | "size" | "updatedAt";

type FileSystemSortState = {
  direction: "asc" | "desc";
  key: FileSystemSortKey;
};

const SORT_OPTIONS: Array<{
  defaultDirection: "asc" | "desc";
  key: FileSystemSortKey;
  label: string;
  /** Shorter label so the toolbar trigger stays narrow. */
  triggerLabel: string;
}> = [
  { defaultDirection: "asc", key: "name", label: "Name", triggerLabel: "Name" },
  {
    defaultDirection: "desc",
    key: "createdAt",
    label: "Date created",
    triggerLabel: "Created",
  },
  {
    defaultDirection: "desc",
    key: "updatedAt",
    label: "Date modified",
    triggerLabel: "Modified",
  },
  {
    defaultDirection: "desc",
    key: "size",
    label: "Size",
    triggerLabel: "Size",
  },
];

const DEFAULT_SORT: FileSystemSortState = { direction: "asc", key: "name" };

function defaultSortDirection(key: FileSystemSortKey) {
  return (
    SORT_OPTIONS.find((option) => option.key === key)?.defaultDirection ?? "asc"
  );
}

function entrySortTimestamp(
  entry: FileSystemEntry,
  key: "createdAt" | "updatedAt",
) {
  const value = entry[key];
  const time = value ? Date.parse(value) : Number.NaN;

  return Number.isNaN(time) ? 0 : time;
}

// Primary key per the active sort; ties (and missing metadata) fall back to
// the name order so results stay stable. The name tiebreak ignores the
// direction, like Finder.
function compareEntriesBySort(
  left: FileSystemEntry,
  right: FileSystemEntry,
  sort: FileSystemSortState,
) {
  const result = ((): number => {
    if (sort.key === "name") {
      return compareEntryNames(left, right);
    }
    if (sort.key === "size") {
      // Folders have no byte size; group them at the small end.
      const leftSize = left.kind === "file" ? (left.size ?? 0) : -1;
      const rightSize = right.kind === "file" ? (right.size ?? 0) : -1;

      return leftSize - rightSize;
    }
    return (
      entrySortTimestamp(left, sort.key) - entrySortTimestamp(right, sort.key)
    );
  })();

  if (result === 0) return compareEntryNames(left, right);
  return sort.direction === "asc" ? (result < 0 ? -1 : 1) : result < 0 ? 1 : -1;
}

export type FileSystemFilterType = "dateCreated" | "dateModified" | "fileType";

type FileSystemDateFilterType = Exclude<FileSystemFilterType, "fileType">;

type FileSystemFilterOperator =
  | "after"
  | "before"
  | "in-range"
  | "is"
  | "is-any-of"
  | "is-not"
  | "not-in-range";

type FileSystemFilter = {
  id: string;
  operator: FileSystemFilterOperator;
  type: FileSystemFilterType;
  value: string[];
};

type FileTypeFilterOption = {
  /** Sample file name so the option icon reuses the file-type sprite. */
  iconFileName: string;
  label: string;
  mime: string;
};

const FILTER_TYPE_LABELS: Record<FileSystemFilterType, string> = {
  dateCreated: "Date created",
  dateModified: "Date modified",
  fileType: "File type",
};

const FILTER_OPERATOR_LABELS: Record<FileSystemFilterOperator, string> = {
  after: "after",
  before: "before",
  "in-range": "in range",
  is: "is",
  "is-any-of": "is any of",
  "is-not": "is not",
  "not-in-range": "not in range",
};

// Relative cutoffs for the date filters, mirroring Extend's table filters.
const DATE_FILTER_PRESETS = [
  "1 day ago",
  "3 days ago",
  "1 week ago",
  "1 month ago",
  "3 months ago",
  "6 months ago",
  "1 year ago",
];

function dateFilterPresetCutoff(preset: string) {
  const date = new Date();

  switch (preset) {
    case "1 day ago":
      date.setDate(date.getDate() - 1);
      break;
    case "3 days ago":
      date.setDate(date.getDate() - 3);
      break;
    case "1 week ago":
      date.setDate(date.getDate() - 7);
      break;
    case "1 month ago":
      date.setMonth(date.getMonth() - 1);
      break;
    case "3 months ago":
      date.setMonth(date.getMonth() - 3);
      break;
    case "6 months ago":
      date.setMonth(date.getMonth() - 6);
      break;
    case "1 year ago":
      date.setFullYear(date.getFullYear() - 1);
      break;
    default: {
      const parsed = Date.parse(preset);

      if (!Number.isNaN(parsed)) return new Date(parsed);
    }
  }
  return date;
}

// Custom ranges store two ISO timestamps instead of a relative preset.
function isCustomDateRangeValue(value: string[]) {
  return (
    value.length === 2 &&
    value.every(
      (entry) =>
        !DATE_FILTER_PRESETS.includes(entry) &&
        !Number.isNaN(Date.parse(entry)),
    )
  );
}

function filterOperatorChoices(
  filter: FileSystemFilter,
): FileSystemFilterOperator[] {
  if (filter.type === "fileType") {
    return filter.value.length > 1 ? ["is-any-of", "is-not"] : ["is", "is-not"];
  }
  if (isCustomDateRangeValue(filter.value)) return ["in-range", "not-in-range"];
  return ["before", "after"];
}

function fileMatchesFilter(file: FileEntry, filter: FileSystemFilter) {
  if (filter.value.length === 0) return true;
  if (filter.type === "fileType") {
    const matches = filter.value.includes(mimeTypeForFile(file));

    return filter.operator === "is-not" ? !matches : matches;
  }

  const timestamp =
    filter.type === "dateCreated" ? file.createdAt : file.updatedAt;
  const time = timestamp ? Date.parse(timestamp) : Number.NaN;

  if (Number.isNaN(time)) return false;
  if (filter.operator === "in-range" || filter.operator === "not-in-range") {
    const from = Date.parse(filter.value[0]);
    const to = Date.parse(filter.value[1] ?? filter.value[0]);
    const isInRange = time >= from && time <= to;

    return filter.operator === "not-in-range" ? !isInRange : isInRange;
  }

  const cutoff = dateFilterPresetCutoff(filter.value[0]).getTime();

  return filter.operator === "before" ? time <= cutoff : time >= cutoff;
}

function buildFileSystemIndex(items: FileSystemItem[]): FileSystemIndex {
  const folders = new Map<string, FolderEntry>();
  const files = new Map<string, FileEntry>();

  const ensureFolderChain = (folderPath: string) => {
    let path = normalizeFolderPath(folderPath);

    while (path && !folders.has(path)) {
      folders.set(path, {
        kind: "folder",
        name: pathName(path),
        parentPath: pathParent(path),
        path,
      });
      path = pathParent(path);
    }
  };

  for (const item of items) {
    if (item.kind === "folder") {
      const path = normalizeFolderPath(item.path);

      if (!path) continue;

      folders.set(path, {
        ...item,
        name: item.name ?? pathName(path),
        parentPath: normalizeFolderPath(item.parentPath ?? pathParent(path)),
        path,
      });
      ensureFolderChain(pathParent(path));
    } else {
      if (!item.path) continue;

      files.set(item.path, {
        ...item,
        key: item.key ?? item.path,
        name: item.name ?? pathName(item.path),
        parentPath: normalizeFolderPath(
          item.parentPath ?? pathParent(item.path),
        ),
      });
      ensureFolderChain(pathParent(item.path));
    }
  }

  const children = new Map<string, FileSystemEntry[]>();
  const pushChild = (entry: FileSystemEntry) => {
    const siblings = children.get(entry.parentPath);

    if (siblings) {
      siblings.push(entry);
    } else {
      children.set(entry.parentPath, [entry]);
    }
  };

  for (const folder of folders.values()) pushChild(folder);
  for (const file of files.values()) pushChild(file);
  for (const siblings of children.values()) {
    siblings.sort(compareEntryNames);
  }

  return { children, files, folders };
}

function folderHasChildren(index: FileSystemIndex, folder: FolderEntry) {
  return (
    (index.children.get(folder.path)?.length ?? 0) > 0 ||
    folder.hasChildren === true
  );
}

// Morgan extension: the folder glyph is recolored from the upstream macOS
// blue to the app's brand green (--primary, hsl(128 38% 17%)). Two palettes
// — `[backTop, backBottom, frontTop, frontBottom]` gradient stops — cover
// light surfaces (the brand dark green) and dark/primary surfaces (a
// lighter green of the same hue, so the folder stays legible on dark
// backgrounds and on the primary selection pill).
type FolderGlyphPalette = readonly [string, string, string, string];

const FOLDER_GLYPH_LIGHT: FolderGlyphPalette = [
  "#356e3d",
  "#1b3c1f",
  "#54995f",
  "#356e3d",
];
const FOLDER_GLYPH_DARK: FolderGlyphPalette = [
  "#6cab79",
  "#41804d",
  "#95cb9f",
  "#6cab79",
];

// Shared geometry: back panel (with the tab) and front panel paths.
const FOLDER_GLYPH_BACK_PATH =
  "M5 10c0-3.31 2.69-6 6-6h10.9c1.6 0 3.13.7 4.18 1.9l1.5 1.73a3.5 3.5 0 0 0 2.64 1.22H54c2.76 0 5 2.24 5 5V40c0 3.87-3.13 7-7 7H12c-3.87 0-7-3.13-7-7V10Z";
const FOLDER_GLYPH_FRONT_PATH =
  "M5 15.5h54V40c0 3.87-3.13 7-7 7H12c-3.87 0-7-3.13-7-7V15.5Z";

// Standalone SVG sources for the @pierre/trees shadow DOM, where the glyph
// renders as a CSS url() background (data URLs cannot read CSS variables,
// so each palette bakes its colors in).
function folderGlyphSvg([
  backTop,
  backBottom,
  frontTop,
  frontBottom,
]: FolderGlyphPalette) {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 50" width="64" height="50"><defs><linearGradient id="fs-folder-back" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stop-color="${backTop}"/><stop offset="1" stop-color="${backBottom}"/></linearGradient><linearGradient id="fs-folder-front" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stop-color="${frontTop}"/><stop offset="1" stop-color="${frontBottom}"/></linearGradient></defs><path d="${FOLDER_GLYPH_BACK_PATH}" fill="url(#fs-folder-back)"/><path d="${FOLDER_GLYPH_FRONT_PATH}" fill="url(#fs-folder-front)"/></svg>`;
}

const FOLDER_GLYPH_DATA_URL_LIGHT = `data:image/svg+xml,${encodeURIComponent(folderGlyphSvg(FOLDER_GLYPH_LIGHT))}`;
const FOLDER_GLYPH_DATA_URL_DARK = `data:image/svg+xml,${encodeURIComponent(folderGlyphSvg(FOLDER_GLYPH_DARK))}`;

// Light-DOM folder glyph: an inline SVG whose gradient stops read the
// --fs-folder-* variables (declared in FILE_ICON_COLOR_CSS), so it follows
// the same light/dark/on-light/on-primary palette flips as the file icons.
// Width/height attributes keep the img-like intrinsic 64×50 ratio for the
// `h-* w-auto` call sites; gradient ids are instance-scoped via useId.
function FileSystemFolderGlyph({ className }: { className?: string }) {
  // useId's delimiters (":" in React 18, "«»" in 19) are stripped — they
  // are unreliable inside SVG url(#…) gradient references.
  const gradientId = React.useId().replace(/[^a-zA-Z0-9-]/g, "");
  const backId = `fs-folder-${gradientId}-back`;
  const frontId = `fs-folder-${gradientId}-front`;

  return (
    <svg
      viewBox="0 0 64 50"
      width="64"
      height="50"
      aria-hidden="true"
      className={className}
    >
      <defs>
        <linearGradient id={backId} x1="0" x2="0" y1="0" y2="1">
          <stop
            offset="0"
            style={{
              stopColor: `var(--fs-folder-back-top, ${FOLDER_GLYPH_LIGHT[0]})`,
            }}
          />
          <stop
            offset="1"
            style={{
              stopColor: `var(--fs-folder-back-bottom, ${FOLDER_GLYPH_LIGHT[1]})`,
            }}
          />
        </linearGradient>
        <linearGradient id={frontId} x1="0" x2="0" y1="0" y2="1">
          <stop
            offset="0"
            style={{
              stopColor: `var(--fs-folder-front-top, ${FOLDER_GLYPH_LIGHT[2]})`,
            }}
          />
          <stop
            offset="1"
            style={{
              stopColor: `var(--fs-folder-front-bottom, ${FOLDER_GLYPH_LIGHT[3]})`,
            }}
          />
        </linearGradient>
      </defs>
      <path d={FOLDER_GLYPH_BACK_PATH} fill={`url(#${backId})`} />
      <path d={FOLDER_GLYPH_FRONT_PATH} fill={`url(#${frontId})`} />
    </svg>
  );
}

function escapeXmlAttribute(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// Morgan extension: stable stand-in for a trivial multi-selection so the
// memoized columns don't re-render on every plain (single) selection move.
// Declared with the module constants — before every component that reads
// it — so HMR/Fast Refresh re-evaluation can never hit it uninitialized.
const EMPTY_PATH_SET: ReadonlySet<string> = new Set();

// The @pierre/trees "complete" set — the full, colored suite with brand and
// framework glyphs — ships as an SVG sprite. The list view tree consumes it
// natively inside its shadow DOM; the icon, column, and gallery views render
// the same sprite from the light DOM so every view falls back to the same
// file-type icon when a file has no thumbnail.
// Morgan extension: the set is code-file oriented (pdf/doc/msg all resolve
// to the gray "default" token), so Morgan's business-document symbols and a
// shared `byFileExtension` remap ride along — see `file-system-icons.ts`.
const FILE_ICON_SPRITE_SHEET =
  getBuiltInSpriteSheet("complete") + MORGAN_FILE_ICON_SPRITE_SHEET;

const { resolveIcon: resolveFileIcon } = createFileTreeIconResolver({
  byFileExtension: MORGAN_FILE_ICON_EXTENSIONS,
  colored: true,
  set: "complete",
});

// Per-token light/dark colors mirroring the palette the tree applies inside
// its shadow DOM. Tokens without an entry (font, nextjs, stylelint) stay
// muted-foreground there too. Morgan extension: the business-document
// tokens (pdf, word, excel, powerpoint, email) merge in at the end; their
// symbols self-color via these vars rather than `data-icon-token` CSS.
const FILE_ICON_COLORS: Record<string, [light: string, dark: string]> = {
  astro: ["#a631be", "#d568ea"],
  babel: ["#d5a910", "#ffd452"],
  bash: ["#199f43", "#5ecc71"],
  biome: ["#1a85d4", "#69b1ff"],
  bootstrap: ["#693acf", "#9d6afb"],
  browserslist: ["#d5a910", "#ffd452"],
  bun: ["#594c5b", "#79697b"],
  c: ["#1a85d4", "#69b1ff"],
  claude: ["#d47628", "#ffa359"],
  cpp: ["#1a85d4", "#69b1ff"],
  css: ["#693acf", "#9d6afb"],
  database: ["#a631be", "#d568ea"],
  default: ["#84848a", "#adadb1"],
  docker: ["#1a85d4", "#69b1ff"],
  eslint: ["#693acf", "#9d6afb"],
  git: ["#ff8c5b", "#d5512f"],
  go: ["#1ca1c7", "#68cdf2"],
  graphql: ["#d32a61", "#ff678d"],
  html: ["#d47628", "#ffa359"],
  image: ["#d32a61", "#ff678d"],
  javascript: ["#d5a910", "#ffd452"],
  json: ["#d47628", "#ffa359"],
  markdown: ["#199f43", "#5ecc71"],
  mcp: ["#17a5af", "#64d1db"],
  npm: ["#d52c36", "#ff6762"],
  oxc: ["#1ca1c7", "#68cdf2"],
  postcss: ["#d52c36", "#ff6762"],
  prettier: ["#17a5af", "#64d1db"],
  python: ["#1a85d4", "#69b1ff"],
  react: ["#1ca1c7", "#68cdf2"],
  ruby: ["#d52c36", "#ff6762"],
  rust: ["#d47628", "#ffa359"],
  sass: ["#d32a61", "#ff678d"],
  svelte: ["#d52c36", "#ff6762"],
  svg: ["#d47628", "#ffa359"],
  svgo: ["#199f43", "#5ecc71"],
  swift: ["#d47628", "#ffa359"],
  table: ["#17a5af", "#64d1db"],
  tailwind: ["#1ca1c7", "#68cdf2"],
  terraform: ["#693acf", "#9d6afb"],
  text: ["#84848a", "#adadb1"],
  typescript: ["#1a85d4", "#69b1ff"],
  vite: ["#a631be", "#d568ea"],
  vscode: ["#1a85d4", "#69b1ff"],
  vue: ["#199f43", "#5ecc71"],
  wasm: ["#693acf", "#9d6afb"],
  webpack: ["#1a85d4", "#69b1ff"],
  yml: ["#d52c36", "#ff6762"],
  zig: ["#d47628", "#ffa359"],
  zip: ["#d47628", "#ffa359"],
  ...MORGAN_FILE_ICON_COLORS,
};

function fileIconColorVariables(mode: 0 | 1) {
  return Object.entries(FILE_ICON_COLORS)
    .map(([token, colors]) => `--fs-file-icon-${token}: ${colors[mode]};`)
    .join(" ");
}

// Morgan extension: folder-glyph palette variables, flipped alongside the
// file-icon palettes. The gradient-stop variables feed the inline light-DOM
// glyph; the url() variables feed the tree's shadow-DOM ::before background
// (custom properties inherit through shadow boundaries, data URLs cannot
// read them — hence both forms).
function folderGlyphVariables(mode: 0 | 1) {
  const palette = mode === 0 ? FOLDER_GLYPH_LIGHT : FOLDER_GLYPH_DARK;
  const glyphUrl =
    mode === 0 ? FOLDER_GLYPH_DATA_URL_LIGHT : FOLDER_GLYPH_DATA_URL_DARK;
  const onPrimaryGlyphUrl =
    mode === 0 ? FOLDER_GLYPH_DATA_URL_DARK : FOLDER_GLYPH_DATA_URL_LIGHT;

  return (
    `--fs-folder-back-top: ${palette[0]}; ` +
    `--fs-folder-back-bottom: ${palette[1]}; ` +
    `--fs-folder-front-top: ${palette[2]}; ` +
    `--fs-folder-front-bottom: ${palette[3]}; ` +
    `--fs-folder-glyph: url("${glyphUrl}"); ` +
    `--fs-folder-glyph-on-primary: url("${onPrimaryGlyphUrl}");`
  );
}

// The variables live on :root rather than the component root because the
// filter menus and dialogs portal outside it; the --fs-file-icon-*
// namespace keeps them collision-free. Thumbnail tiles keep a light
// (paper) surface in dark mode, so icons inside them revert to the light
// palette ([data-file-system-on-light]); selected rows sit on the primary
// surface — the opposite of the mode's background — so icons there swap to
// the opposite palette ([data-file-system-on-primary] in the light DOM,
// --fs-selected-color-scheme for the tree's light-dark() colors inside its
// shadow DOM).
const FILE_ICON_COLOR_CSS = `
:root { ${fileIconColorVariables(0)} ${folderGlyphVariables(0)} --fs-selected-color-scheme: dark; }
.dark { ${fileIconColorVariables(1)} ${folderGlyphVariables(1)} --fs-selected-color-scheme: light; }
.dark [data-file-system-on-light] { ${fileIconColorVariables(0)} ${folderGlyphVariables(0)} }
[data-file-system-on-primary] { ${fileIconColorVariables(1)} ${folderGlyphVariables(1)} }
.dark [data-file-system-on-primary] { ${fileIconColorVariables(0)} ${folderGlyphVariables(0)} }
`;

function FileSystemIconSpriteSheet() {
  return (
    <>
      <span
        aria-hidden="true"
        className="hidden"
        // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml -- static SVG sprite sheet: @pierre/trees built-ins plus typed icon constants from file-system-icons.ts, no external input
        dangerouslySetInnerHTML={{ __html: FILE_ICON_SPRITE_SHEET }}
      />
      <style>{FILE_ICON_COLOR_CSS}</style>
    </>
  );
}

function FileTypeIcon({
  fileName,
  className,
}: {
  fileName: string;
  className?: string;
}) {
  const icon = resolveFileIcon("file-tree-icon-file", fileName);

  return (
    <svg
      aria-hidden="true"
      viewBox={icon.viewBox ?? "0 0 16 16"}
      className={cn("shrink-0 text-muted-foreground", className)}
      style={
        icon.token
          ? {
              color: `var(--fs-file-icon-${icon.token}, var(--color-muted-foreground))`,
            }
          : undefined
      }
    >
      <use href={`#${icon.name}`} />
    </svg>
  );
}

function FileGenericPreview({ file }: { file: FileEntry }) {
  const extension = fileExtension(file.name);

  return (
    <div
      data-file-system-on-light=""
      className="flex size-full flex-col items-center justify-center gap-1.5 bg-white text-neutral-400 dark:bg-neutral-100"
    >
      <FileTypeIcon fileName={file.name} className="size-1/3 min-h-4 min-w-4" />
      {extension ? (
        <span className="text-[min(0.625rem,18cqw)] font-semibold uppercase tracking-wide">
          {extension}
        </span>
      ) : null}
    </div>
  );
}

function filePreviewUrls(file: FileSystemFileItem) {
  if (file.previewImageUrls?.length) return file.previewImageUrls;
  return file.previewImageUrl ? [file.previewImageUrl] : [];
}

// Mirrors @pierre/trees' query normalization so the toolbar search filters
// the icon, column, and gallery views exactly like the list view tree:
// trimmed, backslashes to slashes, lowercased, substring match on the path.
function normalizeSearchQuery(value: string) {
  const trimmed = value.trim();

  if (!trimmed) return "";
  return trimmed.replace(/\\/g, "/").toLowerCase();
}

// Windowed rendering, the approach @pierre/trees uses for the list view:
// with a fixed item stride only the items intersecting the viewport — plus
// `overscan` on each side — are mounted, so views stay flat-cost at
// thousands of entries. The window keeps a one-item margin before
// recomputing (scrolling doesn't re-render per item) and that margin also
// guarantees single-step keyboard moves land on a mounted neighbor.
function useVirtualWindow({
  count,
  horizontal = false,
  itemStride,
  leadingPx = 0,
  overscan = 8,
  viewportRef,
}: {
  count: number;
  horizontal?: boolean;
  itemStride: number;
  leadingPx?: number;
  overscan?: number;
  viewportRef: React.RefObject<HTMLDivElement | null>;
}) {
  const [window_, setWindow] = React.useState(() => ({
    end: Math.min(count, overscan * 2),
    start: 0,
  }));

  React.useLayoutEffect(() => {
    const viewport = viewportRef.current;

    if (!viewport || itemStride <= 0) return;

    const update = () => {
      const scrollStart =
        (horizontal ? viewport.scrollLeft : viewport.scrollTop) - leadingPx;
      const viewportSize = horizontal
        ? viewport.clientWidth
        : viewport.clientHeight;
      const firstVisible = Math.max(0, Math.floor(scrollStart / itemStride));
      const lastVisible = Math.min(
        count,
        Math.ceil((scrollStart + viewportSize) / itemStride),
      );

      setWindow((previous) => {
        if (
          previous.end <= count &&
          previous.start <= Math.max(0, firstVisible - 1) &&
          previous.end >= Math.min(count, lastVisible + 1)
        ) {
          return previous;
        }
        return {
          end: Math.min(count, lastVisible + overscan),
          start: Math.max(0, firstVisible - overscan),
        };
      });
    };

    update();
    viewport.addEventListener("scroll", update, { passive: true });

    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);

    observer?.observe(viewport);
    return () => {
      viewport.removeEventListener("scroll", update);
      observer?.disconnect();
    };
  }, [count, horizontal, itemStride, leadingPx, overscan, viewportRef]);

  return window_;
}

// Scrolls the item at `index` into the viewport when it sits outside it —
// virtualized views need this because off-window items have no DOM node to
// call scrollIntoView on.
function scrollIndexIntoView({
  horizontal = false,
  index,
  itemSize,
  itemStride,
  leadingPx = 0,
  viewport,
}: {
  horizontal?: boolean;
  index: number;
  itemSize: number;
  itemStride: number;
  leadingPx?: number;
  viewport: HTMLDivElement | null;
}) {
  if (!viewport || index < 0) return;

  const start = leadingPx + index * itemStride;
  const end = start + itemSize;
  const scrollStart = horizontal ? viewport.scrollLeft : viewport.scrollTop;
  const viewportSize = horizontal
    ? viewport.clientWidth
    : viewport.clientHeight;

  const nextScrollStart: number | null =
    start < scrollStart
      ? start
      : end > scrollStart + viewportSize
        ? end - viewportSize
        : null;

  if (nextScrollStart === null) return;
  if (horizontal) {
    viewport.scrollLeft = nextScrollStart;
  } else {
    viewport.scrollTop = nextScrollStart;
  }
}

function FileVisual({
  file,
  className,
  loadPreviewImageUrl,
  pageable = false,
  pageUrlCache,
  previewAspectRatio,
  previewClassName,
  renderFilePreview,
}: {
  file: FileEntry;
  className?: string;
  loadPreviewImageUrl?: (
    file: FileSystemFileItem,
    pageIndex: number,
  ) => Promise<string | null>;
  /** Show a hover pager over multi-page thumbnails. */
  pageable?: boolean;
  /**
   * Shared `"path#pageIndex"` → URL cache so pages fetched by one pager
   * (gallery stage, columns preview) are reused by every other instance.
   */
  pageUrlCache?: Map<string, string>;
  previewAspectRatio?: number;
  previewClassName?: string;
  renderFilePreview?: (file: FileSystemFileItem) => React.ReactNode;
}) {
  const previewUrls = filePreviewUrls(file);
  const canLoadLazily = pageable && Boolean(loadPreviewImageUrl);
  const totalPages = Math.max(
    previewUrls.length,
    canLoadLazily ? (file.previewPageCount ?? 0) : 0,
  );
  const [pageIndex, setPageIndex] = React.useState(0);
  const [lazyPageUrls, setLazyPageUrls] = React.useState<
    Record<number, string>
  >({});
  // Morgan extension: pages whose lazy load resolved to null. Without this a
  // failed load (CORS, broken PDF) would stay "pending" forever and pin the
  // thumbnail on its spinner instead of falling back to the type icon.
  const [failedPages, setFailedPages] = React.useState<Record<number, true>>(
    {},
  );
  const clampedPageIndex = Math.min(pageIndex, Math.max(totalPages - 1, 0));
  const previewUrl =
    previewUrls[clampedPageIndex] ??
    lazyPageUrls[clampedPageIndex] ??
    pageUrlCache?.get(`${file.path}#${clampedPageIndex}`) ??
    null;
  const resolvedAspectRatio = file.previewAspectRatio ?? previewAspectRatio;
  const isLazyPagePending =
    canLoadLazily &&
    !previewUrl &&
    clampedPageIndex < totalPages &&
    !failedPages[clampedPageIndex];

  const fileRef = React.useRef(file);

  React.useEffect(() => {
    fileRef.current = file;
  });

  React.useEffect(() => {
    setPageIndex(0);
    setLazyPageUrls({});
    setFailedPages({});
  }, [file.path]);

  // Keyed by path (not object identity) so manifest churn doesn't re-request
  // the page already being loaded.
  React.useEffect(() => {
    if (!isLazyPagePending || !loadPreviewImageUrl) return;

    let isCurrent = true;

    void loadPreviewImageUrl(fileRef.current, clampedPageIndex)
      .then((url) => {
        // Cache even when stale (page flipped away mid-load): the fetch is
        // done, so let the next visit use it.
        if (url) pageUrlCache?.set(`${file.path}#${clampedPageIndex}`, url);
        if (isCurrent && url) {
          setLazyPageUrls((previous) => ({
            ...previous,
            [clampedPageIndex]: url,
          }));
        }
        // Morgan extension: a null result means "no preview available" —
        // record it so the thumbnail settles on the type icon.
        if (isCurrent && !url) {
          setFailedPages((previous) => ({
            ...previous,
            [clampedPageIndex]: true,
          }));
        }
      })
      .catch(() => {
        // Morgan extension: treat rejections like null results.
        if (isCurrent) {
          setFailedPages((previous) => ({
            ...previous,
            [clampedPageIndex]: true,
          }));
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [
    clampedPageIndex,
    file.path,
    isLazyPagePending,
    loadPreviewImageUrl,
    pageUrlCache,
  ]);

  const customPreview =
    !previewUrl && !isLazyPagePending ? renderFilePreview?.(file) : null;
  const showPager = pageable && totalPages > 1;
  const thumbnail = (
    <FileThumbnail
      file={{ name: file.name, type: file.contentType ?? "" }}
      // Upstream marked this a container-query context (`@container`), but no
      // @-variant consumes it and Morgan's Tailwind 3.4 lacks the plugin.
      className={cn(!showPager && className)}
      previewAspectRatio={resolvedAspectRatio}
      previewClassName={cn("bg-white dark:bg-neutral-100", previewClassName)}
      previewImageUrl={previewUrl ?? undefined}
      isLoading={isLazyPagePending}
      previewContent={
        previewUrl || isLazyPagePending
          ? undefined
          : (customPreview ?? <FileGenericPreview file={file} />)
      }
    />
  );

  if (!showPager) return thumbnail;

  return (
    <div className={cn("group/pager relative", className)}>
      {thumbnail}
      <div className="absolute inset-x-0 bottom-1.5 flex items-center justify-center gap-1 opacity-0 transition-opacity group-focus-within/pager:opacity-100 group-hover/pager:opacity-100">
        <button
          type="button"
          aria-label="Previous page"
          tabIndex={-1}
          disabled={clampedPageIndex === 0}
          onClick={(event) => {
            event.stopPropagation();
            setPageIndex((previous) => Math.max(0, previous - 1));
          }}
          onDoubleClick={(event) => event.stopPropagation()}
          className="flex size-6 items-center justify-center rounded-md bg-background/80 text-foreground shadow-sm outline-none backdrop-blur-sm transition-colors hover:bg-background focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40"
        >
          <HugeiconsIcon icon={ArrowLeft01Icon} className="size-3.5" />
        </button>
        <span className="rounded-md bg-background/80 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-muted-foreground shadow-sm backdrop-blur-sm">
          {clampedPageIndex + 1}/{totalPages}
        </span>
        <button
          type="button"
          aria-label="Next page"
          tabIndex={-1}
          disabled={clampedPageIndex >= totalPages - 1}
          onClick={(event) => {
            event.stopPropagation();
            setPageIndex((previous) => Math.min(totalPages - 1, previous + 1));
          }}
          onDoubleClick={(event) => event.stopPropagation()}
          className="flex size-6 items-center justify-center rounded-md bg-background/80 text-foreground shadow-sm outline-none backdrop-blur-sm transition-colors hover:bg-background focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40"
        >
          <HugeiconsIcon icon={ArrowRight01Icon} className="size-3.5" />
        </button>
      </div>
    </div>
  );
}

const VIEW_OPTIONS: Array<{
  icon: React.ComponentProps<typeof HugeiconsIcon>["icon"];
  label: string;
  value: FileSystemView;
}> = [
  { icon: GridViewIcon, label: "Grid", value: "icons" },
  { icon: LeftToRightListBulletIcon, label: "List", value: "list" },
  { icon: LayoutThreeColumnIcon, label: "Columns", value: "columns" },
  { icon: GalleryThumbnailsIcon, label: "Gallery", value: "gallery" },
];

function isFileSystemView(value: string): value is FileSystemView {
  return VIEW_OPTIONS.some((option) => option.value === value);
}

export function FileSystem({
  items,
  className,
  title = "Files",
  defaultView = "icons",
  view: viewProp,
  onViewChange,
  defaultPath = "",
  onCurrentPathChange,
  onSelectionChange,
  onFileOpen,
  getFileUrl,
  loadChildren,
  loadPreviewImageUrl,
  renderFilePreview,
  // Morgan extension props.
  renderItemColumns,
  getItemClassName,
  itemColumnsHeader,
  renderPreviewDetails,
  renderLargePreview,
  renderItemMenu,
  onSelectionItemsChange,
  onMoveItems,
  onExternalDropOnFolder,
  readOnly = false,
  toolbarStart,
  toolbarEnd,
  hideSearch = false,
  hideFilter = false,
}: FileSystemProps) {
  const [internalView, setInternalView] = React.useState(defaultView);
  const view = viewProp ?? internalView;
  const setView = React.useCallback(
    (nextView: FileSystemView) => {
      setInternalView(nextView);
      onViewChange?.(nextView);
    },
    [onViewChange],
  );

  const [loadedItems, setLoadedItems] = React.useState<FileSystemItem[]>([]);
  const allItems = React.useMemo(
    () => (loadedItems.length ? [...items, ...loadedItems] : items),
    [items, loadedItems],
  );
  const index = React.useMemo(() => buildFileSystemIndex(allItems), [allItems]);

  const [history, setHistory] = React.useState(() => ({
    index: 0,
    stack: [normalizeFolderPath(defaultPath)],
  }));
  const currentPath = history.stack[history.index] ?? "";
  const canGoBack = history.index > 0;
  const canGoForward = history.index < history.stack.length - 1;

  // Morgan extension: every navigation route (navigateTo, back/forward,
  // breadcrumb) updates `history`, so observing the derived `currentPath`
  // covers them all.
  React.useEffect(() => {
    onCurrentPathChange?.(currentPath);
  }, [currentPath, onCurrentPathChange]);

  const [selectedPath, setSelectedPath] = React.useState<string | null>(null);
  const selectedEntry = React.useMemo(() => {
    if (selectedPath === null) return null;

    return (
      index.files.get(selectedPath) ?? index.folders.get(selectedPath) ?? null
    );
  }, [index, selectedPath]);

  // Morgan extension: the full (multi-) selection. Always a superset of
  // `selectedPath` (the primary item). Plain clicks collapse it to one
  // path; cmd/ctrl-clicks toggle membership and shift-clicks extend a
  // range (icons + extended list views; the tree manages its own set).
  const [selectedPaths, setSelectedPaths] = React.useState<readonly string[]>(
    [],
  );
  const selectedPathsRef = React.useRef<readonly string[]>([]);

  const [searchInput, setSearchInput] = React.useState("");
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  const [isSearchExpanded, setIsSearchExpanded] = React.useState(false);
  const searchQuery = normalizeSearchQuery(searchInput);
  const isSearching = searchQuery.length > 0;

  const [sort, setSort] = React.useState(DEFAULT_SORT);
  const [filters, setFilters] = React.useState<FileSystemFilter[]>([]);
  const hasActiveFilters = filters.length > 0;

  // Files must pass every active filter; folders stay visible through
  // matching descendants, so the predicate only ever sees files.
  const fileFilter = React.useMemo(() => {
    if (filters.length === 0) return null;
    return (file: FileEntry) =>
      filters.every((filter) => fileMatchesFilter(file, filter));
  }, [filters]);

  // Paths that stay visible while searching or filtering: every file whose
  // currentPath-relative path contains the query — the list view tree's
  // hide-non-matches semantics — and that passes the filters, plus the
  // ancestor folders leading to it. Folder names participate in search
  // matches only when no filters are active; with filters, a folder is only
  // as visible as the files inside it.
  const visiblePaths = React.useMemo(() => {
    if (!isSearching && !fileFilter) return null;

    const visible = new Set<string>();
    const markVisible = (path: string) => {
      let current = path;
      while (current && current !== currentPath && !visible.has(current)) {
        visible.add(current);
        current = pathParent(current);
      }
    };
    const matchesQuery = (path: string) =>
      !isSearching ||
      path.slice(currentPath.length).toLowerCase().includes(searchQuery);

    for (const [path, file] of index.files) {
      if (path === currentPath) continue;
      if (currentPath && !path.startsWith(currentPath)) continue;
      if (!matchesQuery(path)) continue;
      if (fileFilter && !fileFilter(file)) continue;
      markVisible(path);
    }
    if (!fileFilter) {
      for (const path of index.folders.keys()) {
        if (path === currentPath) continue;
        if (currentPath && !path.startsWith(currentPath)) continue;
        if (matchesQuery(path)) markVisible(path);
      }
    }
    return visible;
  }, [currentPath, fileFilter, index, isSearching, searchQuery]);

  const visibleIndex = React.useMemo(() => {
    if (!visiblePaths) return index;

    const children = new Map<string, FileSystemEntry[]>();

    for (const [parentPath, parentChildren] of index.children) {
      const visibleChildren = parentChildren.filter((entry) =>
        visiblePaths.has(entry.path),
      );

      if (visibleChildren.length) children.set(parentPath, visibleChildren);
    }
    return { ...index, children };
  }, [index, visiblePaths]);

  // Children re-sorted per the active sort; the default (name ascending)
  // reuses the index's pre-sorted arrays untouched.
  const sortedIndex = React.useMemo(() => {
    if (
      sort.key === DEFAULT_SORT.key &&
      sort.direction === DEFAULT_SORT.direction
    ) {
      return visibleIndex;
    }

    const children = new Map<string, FileSystemEntry[]>();

    for (const [parentPath, parentChildren] of visibleIndex.children) {
      children.set(
        parentPath,
        [...parentChildren].sort((left, right) =>
          compareEntriesBySort(left, right, sort),
        ),
      );
    }
    return { ...visibleIndex, children };
  }, [sort, visibleIndex]);

  // The ref mirrors the state so re-selecting the same entry (e.g. the
  // pointerdown + click pair the columns view emits per press) stays a
  // no-op without widening the callback's dependencies.
  const selectedPathRef = React.useRef<string | null>(null);
  const selectEntry = React.useCallback(
    (entry: FileSystemEntry | null) => {
      const path = entry?.path ?? null;
      // Morgan extension: a plain select collapses the multi-selection to
      // the one path, so re-selecting the primary entry of a multi-set is
      // no longer a pure no-op.
      const nextPaths = path === null ? [] : [path];
      const sameSet =
        selectedPathsRef.current.length === nextPaths.length &&
        selectedPathsRef.current[0] === nextPaths[0];

      if (selectedPathRef.current === path && sameSet) return;
      if (!sameSet) {
        selectedPathsRef.current = nextPaths;
        setSelectedPaths(nextPaths);
      }
      if (selectedPathRef.current !== path) {
        selectedPathRef.current = path;
        setSelectedPath(path);
        onSelectionChange?.(entry);
      }
    },
    [onSelectionChange],
  );

  // Morgan extension: cmd/ctrl-click toggles an entry in and out of the
  // multi-selection; the primary follows the toggled entry (or the last
  // remaining one after a deselect).
  const toggleSelectEntry = React.useCallback(
    (entry: FileSystemEntry) => {
      const previous = selectedPathsRef.current;
      const isSelected = previous.includes(entry.path);
      const next = isSelected
        ? previous.filter((path) => path !== entry.path)
        : [...previous, entry.path];
      const primaryPath = isSelected
        ? (next[next.length - 1] ?? null)
        : entry.path;

      selectedPathsRef.current = next;
      setSelectedPaths(next);
      if (selectedPathRef.current !== primaryPath) {
        selectedPathRef.current = primaryPath;
        setSelectedPath(primaryPath);
        onSelectionChange?.(
          primaryPath === null
            ? null
            : primaryPath === entry.path
              ? entry
              : (index.files.get(primaryPath) ??
                index.folders.get(primaryPath) ??
                null),
        );
      }
    },
    [index, onSelectionChange],
  );

  // Morgan extension: shift-click selects the display-order range between
  // the current primary (anchor) and the clicked entry. The anchor stays
  // the primary, like Finder.
  const rangeSelectEntry = React.useCallback(
    (entry: FileSystemEntry, orderedEntries: readonly FileSystemEntry[]) => {
      const anchorPath = selectedPathRef.current;
      const anchorIndex = anchorPath
        ? orderedEntries.findIndex((candidate) => candidate.path === anchorPath)
        : -1;

      if (anchorIndex === -1) {
        selectEntry(entry);
        return;
      }

      const targetIndex = orderedEntries.findIndex(
        (candidate) => candidate.path === entry.path,
      );

      if (targetIndex === -1) return;

      const [from, to] =
        anchorIndex <= targetIndex
          ? [anchorIndex, targetIndex]
          : [targetIndex, anchorIndex];
      const next = orderedEntries
        .slice(from, to + 1)
        .map((candidate) => candidate.path);

      selectedPathsRef.current = next;
      setSelectedPaths(next);
    },
    [selectEntry],
  );

  // Morgan extension: wholesale selection replacement used by the tree view
  // (whose model owns multi-select natively) — first entry is the primary.
  const selectManyEntries = React.useCallback(
    (entries: readonly FileSystemEntry[]) => {
      const next = entries.map((entry) => entry.path);
      const sameSet =
        next.length === selectedPathsRef.current.length &&
        next.every((path, i) => selectedPathsRef.current[i] === path);

      if (!sameSet) {
        selectedPathsRef.current = next;
        setSelectedPaths(next);
      }

      const primary = entries[0] ?? null;

      if (selectedPathRef.current !== (primary?.path ?? null)) {
        selectedPathRef.current = primary?.path ?? null;
        setSelectedPath(primary?.path ?? null);
        onSelectionChange?.(primary);
      }
    },
    [onSelectionChange],
  );

  // A query or filter change can hide the selected entry out from under the
  // views.
  React.useEffect(() => {
    if (!visiblePaths) return;
    if (selectedPath && !visiblePaths.has(selectedPath)) {
      selectEntry(null);
      return;
    }
    // Morgan extension: prune hidden paths out of the multi-selection too.
    if (selectedPathsRef.current.some((path) => !visiblePaths.has(path))) {
      const next = selectedPathsRef.current.filter((path) =>
        visiblePaths.has(path),
      );

      selectedPathsRef.current = next;
      setSelectedPaths(next);
    }
  }, [selectEntry, selectedPath, visiblePaths]);

  // Morgan extension: resolve the multi-selection to entries and surface it.
  const selectedEntries = React.useMemo(() => {
    const entries: FileSystemEntry[] = [];

    for (const path of selectedPaths) {
      const entry = index.files.get(path) ?? index.folders.get(path);

      if (entry) entries.push(entry);
    }
    return entries;
  }, [index, selectedPaths]);

  React.useEffect(() => {
    onSelectionItemsChange?.(selectedEntries);
  }, [onSelectionItemsChange, selectedEntries]);

  const selectedPathsSet = React.useMemo(
    () => new Set(selectedPaths),
    [selectedPaths],
  );

  const applySortKey = React.useCallback((key: FileSystemSortKey) => {
    setSort((previous) =>
      previous.key === key
        ? previous
        : { direction: defaultSortDirection(key), key },
    );
  }, []);

  // Column headers toggle the direction when the column is already active,
  // like Finder.
  const toggleSortColumn = React.useCallback((key: FileSystemSortKey) => {
    setSort((previous) =>
      previous.key === key
        ? { direction: previous.direction === "asc" ? "desc" : "asc", key }
        : { direction: defaultSortDirection(key), key },
    );
  }, []);

  // Distinct MIME types across the loaded manifest, labeled for the filter
  // menu; the first file seen per type lends its name to the option icon.
  const fileTypeOptions = React.useMemo(() => {
    const byMime = new Map<string, FileTypeFilterOption>();

    for (const file of index.files.values()) {
      const mime = mimeTypeForFile(file);

      if (!byMime.has(mime)) {
        byMime.set(mime, {
          iconFileName: file.name,
          label: MIME_TYPE_LABELS[mime] ?? mime,
          mime,
        });
      }
    }
    return [...byMime.values()].sort((left, right) =>
      left.label.localeCompare(right.label),
    );
  }, [index]);

  const filterIdRef = React.useRef(0);
  const [dateRangeDialog, setDateRangeDialog] = React.useState<{
    initialRange?: { from: Date; to: Date };
    type: FileSystemDateFilterType;
  } | null>(null);

  const toggleFileTypeFilterValue = React.useCallback(
    (mime: string, checked: boolean) => {
      const id = `filter-${++filterIdRef.current}`;

      setFilters((previous) => {
        const existing = previous.find((filter) => filter.type === "fileType");

        if (!existing) {
          if (!checked) return previous;
          return [
            ...previous,
            {
              id,
              operator: "is" as const,
              type: "fileType" as const,
              value: [mime],
            },
          ];
        }

        const value = checked
          ? [...new Set([...existing.value, mime])]
          : existing.value.filter((entry) => entry !== mime);

        if (value.length === 0) {
          return previous.filter((filter) => filter !== existing);
        }

        // "is" and "is any of" track the value count; "is not" is unaffected.
        const operator =
          existing.operator === "is" || existing.operator === "is-any-of"
            ? value.length > 1
              ? ("is-any-of" as const)
              : ("is" as const)
            : existing.operator;

        return previous.map((filter) =>
          filter === existing ? { ...filter, operator, value } : filter,
        );
      });
    },
    [],
  );

  const setDatePresetFilter = React.useCallback(
    (type: FileSystemDateFilterType, preset: string) => {
      const id = `filter-${++filterIdRef.current}`;

      setFilters((previous) => [
        ...previous.filter((filter) => filter.type !== type),
        { id, operator: "after", type, value: [preset] },
      ]);
    },
    [],
  );

  // Editing an existing custom range seeds the dialog with its bounds.
  const openDateRangeDialog = React.useCallback(
    (type: FileSystemDateFilterType) => {
      const existing = filters.find((filter) => filter.type === type);

      setDateRangeDialog({
        initialRange:
          existing && isCustomDateRangeValue(existing.value)
            ? {
                from: new Date(existing.value[0]),
                to: new Date(existing.value[1]),
              }
            : undefined,
        type,
      });
    },
    [filters],
  );

  const applyCustomDateRange = React.useCallback(
    (type: FileSystemDateFilterType, from: Date, to: Date) => {
      const id = `filter-${++filterIdRef.current}`;

      setFilters((previous) => {
        const existing = previous.find((filter) => filter.type === type);

        return [
          ...previous.filter((filter) => filter.type !== type),
          {
            id,
            operator:
              existing?.operator === "not-in-range"
                ? ("not-in-range" as const)
                : ("in-range" as const),
            type,
            value: [from.toISOString(), to.toISOString()],
          },
        ];
      });
    },
    [],
  );

  // Below iPad width the view switcher collapses into a select and the sort
  // select drops its label; below 560px the search input collapses into a
  // popover, and below 360px the folder name is dropped too.
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const [headerLayout, setHeaderLayout] = React.useState<
    "full" | "compact" | "minimal"
  >("full");
  const [isBelowIpadWidth, setIsBelowIpadWidth] = React.useState(false);

  React.useEffect(() => {
    const root = rootRef.current;

    if (!root || typeof ResizeObserver === "undefined") return;

    const applyWidth = (width: number | undefined) => {
      if (width === undefined) return;

      setHeaderLayout(
        width < 360 ? "minimal" : width < 560 ? "compact" : "full",
      );
      setIsBelowIpadWidth(width < IPAD_MIN_WIDTH);
    };
    const observer = new ResizeObserver((observerEntries) =>
      applyWidth(observerEntries[0]?.contentRect.width),
    );

    // Measure synchronously so the first painted layout is already correct;
    // the observer then tracks resizes.
    applyWidth(root.clientWidth);
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  const requestedFoldersRef = React.useRef(new Set<string>());
  const [loadingFolders, setLoadingFolders] = React.useState<Set<string>>(
    () => new Set(),
  );
  const ensureChildren = React.useCallback(
    (folderPath: string) => {
      if (!loadChildren) return;

      const folder = index.folders.get(folderPath);

      if (!folder?.hasChildren) return;
      if (index.children.get(folderPath)?.length) return;
      if (requestedFoldersRef.current.has(folderPath)) return;

      requestedFoldersRef.current.add(folderPath);
      setLoadingFolders((previous) => new Set(previous).add(folderPath));

      void (async () => {
        try {
          let cursor: string | null = null;

          do {
            const result = await loadChildren({ cursor, path: folderPath });

            if (result.items.length) {
              setLoadedItems((previous) => [...previous, ...result.items]);
            }
            cursor = result.nextCursor ?? null;
          } while (cursor);
        } catch {
          requestedFoldersRef.current.delete(folderPath);
        } finally {
          setLoadingFolders((previous) => {
            const next = new Set(previous);

            next.delete(folderPath);
            return next;
          });
        }
      })();
    },
    [index, loadChildren],
  );

  const navigateTo = React.useCallback(
    (folderPath: string) => {
      const path = normalizeFolderPath(folderPath);

      setHistory((previous) => {
        if (previous.stack[previous.index] === path) return previous;

        const stack = [...previous.stack.slice(0, previous.index + 1), path];

        return { index: stack.length - 1, stack };
      });
      // Navigation exits search, like Finder.
      setSearchInput("");
      selectEntry(null);
      ensureChildren(path);
    },
    [ensureChildren, selectEntry],
  );

  React.useEffect(() => {
    ensureChildren(currentPath);
  }, [currentPath, ensureChildren]);

  // Navigation unmounts the focused row, dropping focus to <body> and killing
  // the ⌘ shortcuts; reclaim focus onto the component root when that happens.
  const hasNavigatedRef = React.useRef(false);

  React.useEffect(() => {
    if (!hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      return;
    }

    const root = rootRef.current;

    if (root && document.activeElement === document.body) {
      root.focus();
    }
  }, [currentPath]);

  const [openedFile, setOpenedFile] = React.useState<{
    file: FileEntry;
    kind: FileSystemViewerKind;
    url: string;
  } | null>(null);

  // Component-lifetime caches shared by every view and the open dialog:
  // resolved (e.g. presigned) URLs keyed by path, and lazily loaded page
  // thumbnails keyed by `"path#pageIndex"`. Each resolution happens once no
  // matter how often the user revisits a file or switches views; stable
  // URLs also keep the browser's HTTP cache valid for fetched content.
  // Lazy state (never set) rather than refs: the Maps are passed down
  // during render, which the rules of React disallow for ref reads.
  const [resolvedUrlCache] = React.useState(() => new Map<string, string>());
  const [pageUrlCache] = React.useState(() => new Map<string, string>());

  // The keep-alive preview pool. Recently shown documents stay mounted so
  // returning to one — in the gallery stage or the viewer dialog — skips
  // the download and parse work instead of repeating it behind a spinner.
  // Each pooled path renders through a portal into a stable detached <div>
  // created once per path and never swapped (React remounts a portal's
  // children when its container changes); a layout effect reparents that
  // div into whichever host currently shows the file: the gallery's stage
  // wrapper or the open dialog. Imperative appendChild keeps the mounted
  // viewer (and its parsed document) alive across every move. Pooled paths
  // without a current host stay mounted but DETACHED from the DOM — a
  // detached subtree costs no layout, paint, or style-recalc work, so idle
  // pool members never slow down interactions in the visible viewer.
  const [stagePool, setStagePool] = React.useState<string[]>([]);
  const [stageRecency] = React.useState(() => new Map<string, number>());
  const stageClockRef = React.useRef(0);
  const [stageContainers] = React.useState(
    () => new Map<string, HTMLDivElement>(),
  );
  const [stageHosts] = React.useState(() => new Map<string, HTMLElement>());
  const [, bumpStageHosts] = React.useState(0);
  // Bumped on every admission so the attach set recomputes when recency
  // changes without a pool membership change.
  const [stageVersion, setStageVersion] = React.useState(0);
  const [dialogStageHost, setDialogStageHost] =
    React.useState<HTMLElement | null>(null);

  const registerStageHost = React.useCallback(
    (path: string, element: HTMLElement | null) => {
      if (element) {
        if (stageHosts.get(path) === element) return;
        stageHosts.set(path, element);
      } else {
        if (!stageHosts.has(path)) return;
        stageHosts.delete(path);
      }
      bumpStageHosts((version) => version + 1);
    },
    [stageHosts],
  );

  const dialogStageHostRef = React.useCallback(
    (element: HTMLDivElement | null) => setDialogStageHost(element),
    [],
  );

  // Admits a file into the pool (idempotent), evicting the least recently
  // admitted path beyond the cap. The pool array keeps insertion order —
  // reordering would churn the host registrations — so recency lives in a
  // separate map; the version bump re-renders so the attach set below
  // tracks recency even when pool membership is unchanged.
  const poolStagePath = React.useCallback(
    (path: string) => {
      if (!index.files.has(path)) return;
      if (!stageContainers.has(path)) {
        const container = document.createElement("div");

        // Layout/paint containment keeps work inside one preview from
        // invalidating the rest of the page (and vice versa).
        container.className =
          "flex size-full min-h-0 min-w-0 items-center justify-center contain-layout contain-paint";
        stageContainers.set(path, container);
      }

      stageRecency.set(path, ++stageClockRef.current);
      setStageVersion((version) => version + 1);
      setStagePool((previous) => {
        if (previous.includes(path)) return previous;

        const next = [...previous, path];

        if (next.length <= GALLERY_STAGE_POOL_SIZE) return next;

        const evicted = next.reduce((least, candidate) =>
          candidate !== path &&
          (stageRecency.get(candidate) ?? 0) < (stageRecency.get(least) ?? 0)
            ? candidate
            : least,
        );
        return next.filter((candidate) => candidate !== evicted);
      });
    },
    [index, stageContainers, stageRecency],
  );

  const dialogStagePath =
    openedFile !== null && openedFile.kind !== "image"
      ? openedFile.file.path
      : null;
  // Only the most recently shown stages stay attached to the DOM, so
  // rotating among a few files stays instant while older pool members wait
  // detached at zero rendering cost. Memoized so host ref callbacks
  // downstream stay referentially stable — recomputing every render would
  // re-register hosts in a loop.
  const attachedStagePaths = React.useMemo(() => {
    void stageVersion;

    const attached = [...stagePool]
      .sort((a, b) => (stageRecency.get(b) ?? 0) - (stageRecency.get(a) ?? 0))
      .slice(0, GALLERY_STAGE_ATTACHED_COUNT);

    if (
      dialogStagePath &&
      stagePool.includes(dialogStagePath) &&
      !attached.includes(dialogStagePath)
    ) {
      attached.push(dialogStagePath);
    }
    return attached;
  }, [dialogStagePath, stagePool, stageRecency, stageVersion]);

  // Reparent each pooled container to its current host. No dependency
  // array: host registration mutates maps in place, so the cheap loop
  // (pool ≤ GALLERY_STAGE_POOL_SIZE) runs every commit instead of chasing
  // every mutation source.
  React.useLayoutEffect(() => {
    for (const [path, container] of stageContainers) {
      if (!stagePool.includes(path)) {
        // Evicted — React already unmounted the portal's children.
        container.remove();
        stageContainers.delete(path);
        continue;
      }
      if (dialogStagePath === path) {
        // Leave the container in place until the dialog host mounts.
        if (dialogStageHost && container.parentElement !== dialogStageHost) {
          dialogStageHost.appendChild(container);
        }
        continue;
      }

      const target = attachedStagePaths.includes(path)
        ? (stageHosts.get(path) ?? null)
        : null;

      if (!target) {
        if (container.parentElement) container.remove();
      } else if (container.parentElement !== target) {
        target.appendChild(container);
      }
    }
  });

  const openFile = React.useCallback(
    (file: FileEntry) => {
      void (async () => {
        const cachedUrl = file.url ?? resolvedUrlCache.get(file.path) ?? null;
        const url =
          !cachedUrl && getFileUrl
            ? await (async () => {
                try {
                  const resolved = await getFileUrl(file);

                  if (resolved) resolvedUrlCache.set(file.path, resolved);
                  return resolved;
                } catch {
                  return null;
                }
              })()
            : cachedUrl;
        if (onFileOpen) {
          onFileOpen(file, url);
          return;
        }

        const kind = viewerKindForFile(file);

        if (kind && url) {
          // Pool the file so the dialog reuses an already-mounted preview
          // (and the gallery inherits the live viewer after it closes).
          poolStagePath(file.path);
          setOpenedFile({ file, kind, url });
        } else if (url && typeof window !== "undefined") {
          window.open(url, "_blank", "noopener,noreferrer");
        }
      })();
    },
    [getFileUrl, onFileOpen, poolStagePath, resolvedUrlCache],
  );

  const openEntry = React.useCallback(
    (entry: FileSystemEntry) => {
      if (entry.kind === "folder") {
        navigateTo(entry.path);
      } else {
        openFile(entry);
      }
    },
    [navigateTo, openFile],
  );

  // Selecting a lazy folder (columns view, keyboard nav) prefetches children.
  const selectAndPrefetchEntry = React.useCallback(
    (entry: FileSystemEntry | null) => {
      selectEntry(entry);
      if (entry?.kind === "folder") ensureChildren(entry.path);
    },
    [ensureChildren, selectEntry],
  );

  // Morgan extension: tree-view selection replacement with the same lazy
  // children prefetch the single-select path gets.
  const selectManyAndPrefetch = React.useCallback(
    (entries: readonly FileSystemEntry[]) => {
      selectManyEntries(entries);
      if (entries[0]?.kind === "folder") ensureChildren(entries[0].path);
    },
    [ensureChildren, selectManyEntries],
  );

  // Morgan extension: drag-and-drop. Internal drags carry their paths in a
  // ref (dataTransfer payloads are unreadable during dragover); dragging an
  // unselected item selects just it first, dragging a selected item drags
  // the whole selection (Finder behavior).
  const canDragItems = !readOnly && onMoveItems !== undefined;
  const acceptsExternalDrop = !readOnly && onExternalDropOnFolder !== undefined;
  const internalDragPathsRef = React.useRef<readonly string[] | null>(null);

  const beginItemDrag = React.useCallback(
    (entry: FileSystemEntry, dataTransfer: DataTransfer) => {
      const isEntrySelected = selectedPathsRef.current.includes(entry.path);
      const paths = isEntrySelected ? selectedPathsRef.current : [entry.path];

      if (!isEntrySelected) selectEntry(entry);
      internalDragPathsRef.current = paths;
      dataTransfer.effectAllowed = "move";
      dataTransfer.setData(MORGAN_ITEMS_DRAG_TYPE, JSON.stringify(paths));
      dataTransfer.setData("text/plain", paths.join("\n"));
    },
    [selectEntry],
  );

  // Resolves a drop on a folder: internal drags route to `onMoveItems`
  // (skipping self/descendant targets), OS file drags to
  // `onExternalDropOnFolder`.
  const completeFolderDrop = React.useCallback(
    (folderPath: string, dataTransfer: DataTransfer) => {
      const targetPath = normalizeFolderPath(folderPath);

      if (dataTransfer.types.includes(MORGAN_ITEMS_DRAG_TYPE)) {
        const paths = internalDragPathsRef.current ?? [];

        internalDragPathsRef.current = null;
        if (!onMoveItems || paths.length === 0) return;
        for (const path of paths) {
          // Dropping a folder onto itself or one of its descendants is a
          // no-op, like Finder.
          if (path.endsWith("/") && targetPath.startsWith(path)) return;
        }

        const entries: FileSystemEntry[] = [];

        for (const path of paths) {
          const entry = index.files.get(path) ?? index.folders.get(path);

          if (entry) entries.push(entry);
        }
        if (entries.length > 0) onMoveItems(entries, targetPath);
      } else if (dataTransfer.types.includes("Files")) {
        onExternalDropOnFolder?.(dataTransfer, targetPath);
      }
    },
    [index, onExternalDropOnFolder, onMoveItems],
  );

  // Tree-view drops resolve their dragged entries from the tree's own drag
  // session, so they bypass `completeFolderDrop` and call straight through.
  const moveEntries = React.useCallback(
    (entries: readonly FileSystemEntry[], targetFolderPath: string) => {
      if (entries.length > 0) onMoveItems?.([...entries], targetFolderPath);
    },
    [onMoveItems],
  );

  const goBack = React.useCallback(() => {
    setHistory((previous) => ({
      ...previous,
      index: Math.max(0, previous.index - 1),
    }));
    setSearchInput("");
    selectEntry(null);
  }, [selectEntry]);

  const goForward = React.useCallback(() => {
    setHistory((previous) => ({
      ...previous,
      index: Math.min(previous.stack.length - 1, previous.index + 1),
    }));
    setSearchInput("");
    selectEntry(null);
  }, [selectEntry]);

  const currentEntries = sortedIndex.children.get(currentPath) ?? [];
  const currentFolderName =
    currentPath === "" ? title : pathName(currentPath) || title;
  const isLoadingCurrentFolder = loadingFolders.has(currentPath);

  // The list view tree saves its expanded folders here when it unmounts
  // (view switches, navigation) so returning to the list view — or to a
  // previously visited folder — restores the same disclosure state.
  const treeExpansionRef = React.useRef(new Map<string, readonly string[]>());

  const viewProps: FileSystemViewProps = {
    attachedStagePaths,
    currentPath,
    entries: currentEntries,
    fileFilter,
    getFileUrl,
    index: sortedIndex,
    loadPreviewImageUrl,
    loadingFolders,
    onOpen: openEntry,
    onSelect: selectAndPrefetchEntry,
    onSortColumnClick: toggleSortColumn,
    pageUrlCache,
    poolStagePath,
    registerStageHost,
    renderFilePreview,
    searchQuery,
    selectedEntry,
    selectedPath,
    sort,
    treeExpansionRef,
    // Morgan extension fields.
    acceptsExternalDrop,
    canDragItems,
    contextMenuEnabled: !readOnly && renderItemMenu !== undefined,
    itemColumnsHeader,
    onFolderDrop: completeFolderDrop,
    onItemDragStart: beginItemDrag,
    onMoveEntries: moveEntries,
    onRangeSelect: rangeSelectEntry,
    onSelectMany: selectManyAndPrefetch,
    onToggleSelect: toggleSelectEntry,
    renderItemColumns,
    getItemClassName,
    renderPreviewDetails,
    renderLargePreview,
    selectedPathsSet,
  };

  // Morgan extension: resolve the entry behind a contextmenu event — icon
  // tiles, extended list rows, column rows and gallery tiles carry
  // `data-file-system-path` (absolute); the tree's shadow-DOM rows carry
  // `data-item-path` (relative to the open folder), surfaced via
  // composedPath.
  const resolveEntryFromContextEvent = (event: React.MouseEvent) => {
    for (const target of event.nativeEvent.composedPath()) {
      if (!(target instanceof HTMLElement)) continue;

      const absolutePath = target.dataset?.fileSystemPath;

      if (absolutePath) {
        return (
          index.files.get(absolutePath) ??
          index.folders.get(normalizeFolderPath(absolutePath)) ??
          null
        );
      }

      const relativePath = target.dataset?.itemPath;

      if (relativePath) {
        const treePath = `${currentPath}${relativePath}`;

        return (
          index.files.get(treePath) ??
          index.folders.get(normalizeFolderPath(treePath)) ??
          null
        );
      }
    }
    return null;
  };

  const isContextMenuEnabled = !readOnly && renderItemMenu !== undefined;

  const openedFileName = openedFile
    ? (openedFile.file.name ?? openedFile.file.path)
    : "";
  const activeViewOption = VIEW_OPTIONS.find((option) => option.value === view);
  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      data-slot="file-system"
      onKeyDown={(event) => {
        // ⌘F focuses the toolbar search while focus is inside the component.
        // Morgan extension: skipped when the built-in search is hidden.
        if (
          (event.metaKey || event.ctrlKey) &&
          event.key === "f" &&
          !hideSearch
        ) {
          event.preventDefault();
          setIsSearchExpanded(true);
          searchInputRef.current?.focus();
        }
      }}
      className={cn(
        "flex h-[480px] min-h-0 flex-col overflow-hidden rounded-xl border bg-background text-foreground outline-none",
        className,
      )}
    >
      <FileSystemIconSpriteSheet />
      <div
        className={cn(
          "relative grid h-12 shrink-0 items-center gap-2 border-b bg-muted/40 px-2",
          // Default to a centered view-switcher (equal side columns). When a
          // consumer supplies custom toolbar content (e.g. the Library's New
          // Folder / Add Files / Sync actions), let the trailing column size to
          // its content so those buttons are never clipped — the search column
          // absorbs the squeeze instead.
          toolbarStart || toolbarEnd
            ? "grid-cols-[minmax(0,1fr)_auto_auto]"
            : "grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]",
        )}
      >
        <div className="flex min-w-0 items-center gap-0.5">
          <button
            type="button"
            aria-label="Back"
            title="Back"
            disabled={!canGoBack}
            onClick={goBack}
            className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40"
          >
            <HugeiconsIcon icon={ArrowLeft01Icon} className="size-4.5" />
          </button>
          <button
            type="button"
            aria-label="Forward"
            title="Forward"
            disabled={!canGoForward}
            onClick={goForward}
            className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40"
          >
            <HugeiconsIcon icon={ArrowRight01Icon} className="size-4.5" />
          </button>
          {headerLayout !== "minimal" ? (
            <span className="ml-1.5 truncate text-sm font-semibold">
              {currentFolderName}
            </span>
          ) : null}
          {/* Morgan extension: consumer-supplied leading toolbar content. */}
          {toolbarStart ? (
            <div className="ml-2 flex min-w-0 shrink items-center gap-1">
              {toolbarStart}
            </div>
          ) : null}
        </div>
        {headerLayout !== "full" || isBelowIpadWidth ? (
          <Select
            value={view}
            onValueChange={(value) => {
              if (isFileSystemView(value)) setView(value);
            }}
          >
            <SelectTrigger
              aria-label="View"
              // Icon-only like the sort select: sheds the base min-width to
              // hug icon + chevron at the filter button's 28px height.
              className="h-7 min-h-7 w-auto min-w-0 [&_svg]:size-4"
            >
              <SelectValue>
                {activeViewOption ? (
                  <HugeiconsIcon
                    icon={activeViewOption.icon}
                    className="size-4"
                  />
                ) : null}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {VIEW_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <span className="flex items-center gap-2">
                    <HugeiconsIcon icon={option.icon} className="size-4" />
                    {option.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Tabs
            value={view}
            onValueChange={(value) => {
              if (isFileSystemView(value)) setView(value);
            }}
            className="gap-0"
          >
            <TabsList className="h-8 p-0.5">
              {VIEW_OPTIONS.map((option) => (
                <TabsTrigger
                  key={option.value}
                  value={option.value}
                  aria-label={`${option.label} view`}
                  title={option.label}
                  className="h-7 grow-0 px-2.5 sm:h-7"
                >
                  <HugeiconsIcon icon={option.icon} className="size-4" />
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        )}
        <div className="flex min-w-0 items-center justify-end gap-1">
          <FileSystemSortSelect
            layout={headerLayout}
            onKeyChange={applySortKey}
            showLabel={!isBelowIpadWidth}
            sort={sort}
          />
          {/* Morgan extension: hidden when the consumer replaces the
              built-in file-type/date filter with its own control. */}
          {hideFilter ? null : (
            <FileSystemFilterMenu
              fileTypeOptions={fileTypeOptions}
              filters={filters}
              onOpenCustomRange={openDateRangeDialog}
              onSelectDatePreset={setDatePresetFilter}
              onToggleFileType={toggleFileTypeFilterValue}
            />
          )}
          {/* Morgan extension: hidden when the consumer supplies its own
              search via `toolbarEnd`. */}
          {hideSearch ? null : (
            <FileSystemSearchField
              inputRef={searchInputRef}
              isExpanded={isSearchExpanded}
              layout={headerLayout}
              onExpandedChange={setIsSearchExpanded}
              onValueChange={setSearchInput}
              value={searchInput}
            />
          )}
          {/* Morgan extension: consumer-supplied trailing toolbar content. */}
          {toolbarEnd ? (
            <div className="flex min-w-0 shrink items-center gap-1">
              {toolbarEnd}
            </div>
          ) : null}
        </div>
      </div>
      {hasActiveFilters ? (
        <div className="flex shrink-0 flex-wrap items-center gap-1 border-b bg-muted/20 px-2 py-1.5 text-xs text-muted-foreground">
          {filters.map((filter) => {
            const dateFilterType =
              filter.type === "fileType" ? null : filter.type;

            return (
              <FileSystemFilterPill
                key={filter.id}
                fileTypeOptions={fileTypeOptions}
                filter={filter}
                onOpenCustomRange={
                  dateFilterType
                    ? () => openDateRangeDialog(dateFilterType)
                    : undefined
                }
                onOperatorChange={(operator) =>
                  setFilters((previous) =>
                    previous.map((entry) =>
                      entry.id === filter.id ? { ...entry, operator } : entry,
                    ),
                  )
                }
                onRemove={() =>
                  setFilters((previous) =>
                    previous.filter((entry) => entry.id !== filter.id),
                  )
                }
                onSelectDatePreset={(preset) =>
                  setFilters((previous) =>
                    previous.map((entry) =>
                      entry.id === filter.id
                        ? {
                            ...entry,
                            operator:
                              entry.operator === "before" ||
                              entry.operator === "after"
                                ? entry.operator
                                : "after",
                            value: [preset],
                          }
                        : entry,
                    ),
                  )
                }
                onToggleFileType={toggleFileTypeFilterValue}
              />
            );
          })}
          <button
            type="button"
            onClick={() => setFilters([])}
            className="rounded-md px-1.5 py-0.5 outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          >
            Clear
          </button>
        </div>
      ) : null}
      {/* Morgan extension: the views container becomes a ContextMenu
          trigger when an item menu is supplied. The capture handler runs
          before Radix's trigger listener: it selects the right-clicked
          item when it sits outside the current selection, and suppresses
          the custom menu on background right-clicks (stopPropagation keeps
          the event from reaching the trigger, so the native menu shows). */}
      {(() => {
        const viewsContainer = (
          <div
            className="relative min-h-0 flex-1"
            onContextMenuCapture={
              isContextMenuEnabled
                ? (event) => {
                    const entry = resolveEntryFromContextEvent(event);

                    if (!entry) {
                      event.stopPropagation();
                      return;
                    }
                    if (!selectedPathsRef.current.includes(entry.path)) {
                      selectEntry(entry);
                    }
                  }
                : undefined
            }
          >
            {isLoadingCurrentFolder && currentEntries.length === 0 ? (
              <FileSystemEmptyState label="Loading…" isLoading />
            ) : currentEntries.length === 0 &&
              (view !== "columns" || isSearching || hasActiveFilters) ? (
              <FileSystemEmptyState
                label={
                  isSearching
                    ? `No results for “${searchInput.trim()}”`
                    : hasActiveFilters
                      ? "No items match the active filters"
                      : "This folder is empty"
                }
              />
            ) : view === "icons" ? (
              <FileSystemIconsView {...viewProps} />
            ) : view === "list" ? (
              <FileSystemListView {...viewProps} />
            ) : view === "columns" ? (
              <FileSystemColumnsView {...viewProps} />
            ) : (
              <FileSystemGalleryView {...viewProps} />
            )}
          </div>
        );

        if (!isContextMenuEnabled || !renderItemMenu) return viewsContainer;

        return (
          <ContextMenu>
            <ContextMenuTrigger asChild>{viewsContainer}</ContextMenuTrigger>
            {selectedEntries.length > 0 ? (
              <ContextMenuContent className="min-w-52">
                {renderItemMenu(selectedEntries)}
              </ContextMenuContent>
            ) : null}
          </ContextMenu>
        );
      })()}
      <div
        aria-live="polite"
        className="flex h-7 shrink-0 items-center justify-center gap-1 border-t bg-muted/40 px-3 text-xs text-muted-foreground"
      >
        <span>
          {currentEntries.length}{" "}
          {isSearching
            ? currentEntries.length === 1
              ? "result"
              : "results"
            : currentEntries.length === 1
              ? "item"
              : "items"}
        </span>
        {/* Morgan extension: multi-selection count in the status bar. */}
        {selectedPaths.length > 1 ? (
          <span>· {selectedPaths.length} selected</span>
        ) : selectedEntry ? (
          <span>· “{selectedEntry.name}” selected</span>
        ) : null}
      </div>
      <Dialog
        open={openedFile !== null}
        onOpenChange={(open) => {
          if (!open) setOpenedFile(null);
        }}
      >
        {openedFile ? (
          <DialogContent
            className={cn(
              "overflow-hidden p-0",
              VIEWER_DIALOG_CLASSNAMES[openedFile.kind],
            )}
            hideClose={openedFile.kind !== "image"}
          >
            <DialogTitle className="sr-only">{openedFileName}</DialogTitle>
            {openedFile.kind === "image" ? (
              <img
                src={openedFile.url}
                alt={openedFileName}
                className="max-h-[88vh] w-auto max-w-full rounded-2xl object-contain"
              />
            ) : (
              // The pooled preview reparents into this host (see the layout
              // effect above), so a viewer the gallery already loaded
              // carries over live instead of remounting behind a loading
              // state.
              <div
                ref={dialogStageHostRef}
                className="flex h-full min-h-0 flex-1 flex-col"
              />
            )}
          </DialogContent>
        ) : null}
        {/* The pooled previews. Rendered inside <Dialog> so the dialog
            variant's close toolbar button keeps its context; each portal's
            container never changes, the container's parent does. */}
        {stagePool.map((path) => {
          const file = index.files.get(path);
          const container = stageContainers.get(path);

          if (!file || !container) return null;

          return createPortal(
            <FileSystemGalleryStage
              file={file}
              getFileUrl={getFileUrl}
              loadPreviewImageUrl={loadPreviewImageUrl}
              pageUrlCache={pageUrlCache}
              renderFilePreview={renderFilePreview}
              renderLargePreview={renderLargePreview}
              urlCache={resolvedUrlCache}
            />,
            container,
            path,
          );
        })}
      </Dialog>
      {dateRangeDialog ? (
        <FileSystemDateRangeDialog
          initialRange={dateRangeDialog.initialRange}
          onApply={(from, to) => {
            applyCustomDateRange(dateRangeDialog.type, from, to);
            setDateRangeDialog(null);
          }}
          onClose={() => setDateRangeDialog(null)}
        />
      ) : null}
    </div>
  );
}

// Shared style for the ghost icon buttons in the toolbar.
const TOOLBAR_ICON_BUTTON_CLASSNAME =
  "flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors outline-none hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring";

// macOS Finder-style toolbar search. At the full layout it sits inline in
// the header's right column; at compact widths it collapses into a ghost
// icon button that opens the input in a popover (a dot marks the button
// while a query keeps filtering the views).
function FileSystemSearchField({
  inputRef,
  isExpanded,
  layout,
  onExpandedChange,
  onValueChange,
  value,
}: {
  inputRef: React.RefObject<HTMLInputElement>;
  isExpanded: boolean;
  layout: "full" | "compact" | "minimal";
  onExpandedChange: (isExpanded: boolean) => void;
  onValueChange: (value: string) => void;
  value: string;
}) {
  const isInline = layout === "full";

  React.useEffect(() => {
    if (!isInline && isExpanded) inputRef.current?.focus();
  }, [inputRef, isExpanded, isInline]);

  const input = (
    <div
      className={cn(
        // Simplified from the upstream Tailwind v4 styling (not-* variants,
        // --theme()/--spacing() functions, shadow opacity modifiers) which
        // Tailwind 3.4 cannot generate.
        "relative flex h-7 min-w-0 flex-1 items-center rounded-lg border border-input bg-popover text-sm text-foreground shadow-sm transition-shadow focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1 focus-within:ring-offset-background",
        isInline && "max-w-56",
      )}
    >
      <HugeiconsIcon
        icon={Search01Icon}
        className="pointer-events-none absolute left-2 size-3.5 text-muted-foreground"
      />
      <input
        ref={inputRef}
        type="text"
        role="searchbox"
        aria-label="Search files"
        placeholder="Search"
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Escape") return;
          event.preventDefault();
          event.stopPropagation();
          if (value) {
            onValueChange("");
          } else {
            onExpandedChange(false);
            event.currentTarget.blur();
          }
        }}
        className="h-full w-full min-w-0 rounded-[inherit] bg-transparent pl-7 pr-6 outline-none placeholder:text-muted-foreground"
      />
      {value ? (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => {
            onValueChange("");
            inputRef.current?.focus();
          }}
          className="absolute right-1 flex size-5 items-center justify-center rounded-sm text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
        >
          <HugeiconsIcon icon={Cancel01Icon} className="size-3" />
        </button>
      ) : null}
    </div>
  );

  if (isInline) {
    // A fixed basis (not flex-1) keeps the whole toolbar cluster packed
    // against the header's right edge; the input shrinks first when the
    // header tightens.
    return <div className="flex w-56 min-w-32 items-center">{input}</div>;
  }

  return (
    <Popover open={isExpanded} onOpenChange={onExpandedChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Search"
          title="Search"
          className={cn(TOOLBAR_ICON_BUTTON_CLASSNAME, "relative")}
        >
          <HugeiconsIcon icon={Search01Icon} className="size-4" />
          {value ? (
            <span className="absolute right-1 top-1 size-1.5 rounded-full bg-primary" />
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={6} className="w-64 p-1">
        {input}
      </PopoverContent>
    </Popover>
  );
}

// Toolbar "sort by" select. The full layout shows the active key's label; at
// compact widths the trigger collapses to the sort glyph + chevron.
function FileSystemSortSelect({
  layout,
  onKeyChange,
  showLabel,
  sort,
}: {
  layout: "full" | "compact" | "minimal";
  onKeyChange: (key: FileSystemSortKey) => void;
  showLabel: boolean;
  sort: FileSystemSortState;
}) {
  const activeOption = SORT_OPTIONS.find((option) => option.key === sort.key);

  return (
    <Select
      value={sort.key}
      onValueChange={(value) => onKeyChange(value as FileSystemSortKey)}
    >
      <SelectTrigger
        aria-label="Sort by"
        title="Sort by"
        className="h-7 min-h-7 w-auto min-w-0 shrink-0 [&_svg]:size-4"
      >
        <SelectValue>
          <span className="flex items-center gap-1.5">
            <HugeiconsIcon icon={ArrowUpDownIcon} className="size-4" />
            {layout === "full" && showLabel ? activeOption?.triggerLabel : null}
          </span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent align="end">
        {SORT_OPTIONS.map((option) => (
          <SelectItem key={option.key} value={option.key}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Searchable file-type list (cmdk) rendered inside a menu popup, so the
// long MIME list can be filtered by typing. Selection toggles stay open for
// multi-select; ArrowUp/Down and Enter come from cmdk's combobox semantics.
function FileSystemFileTypeCommand({
  checkedMimes,
  onToggle,
  options,
}: {
  checkedMimes: string[];
  onToggle: (mime: string, checked: boolean) => void;
  options: FileTypeFilterOption[];
}) {
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  // The menu focuses its popup when it opens; pull focus into the search
  // field so typing filters immediately.
  React.useEffect(() => {
    const frame = requestAnimationFrame(() => inputRef.current?.focus());

    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <Command
      // -m-1 spans the menu viewport's built-in padding so the search
      // field's bottom border runs edge to edge.
      className="-m-1 w-[calc(100%+0.5rem)] bg-transparent"
      // cmdk owns the keyboard while focus is in the list; only Escape
      // (close the menu) and Tab continue outward.
      onKeyDown={(event) => {
        if (event.key !== "Escape" && event.key !== "Tab") {
          event.stopPropagation();
        }
      }}
    >
      <CommandInput
        ref={inputRef}
        placeholder="Search file types…"
        className="h-9"
      />
      <CommandList className="max-h-none">
        <CommandEmpty>No file types found.</CommandEmpty>
        <ScrollArea orientation="vertical" className="h-auto max-h-64">
          <CommandGroup>
            {options.map((option) => {
              const isChecked = checkedMimes.includes(option.mime);

              return (
                <CommandItem
                  key={option.mime}
                  value={option.label}
                  keywords={[option.mime]}
                  onSelect={() => onToggle(option.mime, !isChecked)}
                >
                  <HugeiconsIcon
                    icon={Tick02Icon}
                    className={cn(
                      "size-4 text-foreground",
                      !isChecked && "opacity-0",
                    )}
                  />
                  <FileTypeIcon
                    fileName={option.iconFileName}
                    className="size-4"
                  />
                  {option.label}
                </CommandItem>
              );
            })}
          </CommandGroup>
        </ScrollArea>
      </CommandList>
    </Command>
  );
}

// Toolbar filter menu: file types as a searchable checklist, dates as
// single-select presets plus a custom range, mirroring Extend's table
// filters.
function FileSystemFilterMenu({
  fileTypeOptions,
  filters,
  onOpenCustomRange,
  onSelectDatePreset,
  onToggleFileType,
}: {
  fileTypeOptions: FileTypeFilterOption[];
  filters: FileSystemFilter[];
  onOpenCustomRange: (type: FileSystemDateFilterType) => void;
  onSelectDatePreset: (type: FileSystemDateFilterType, preset: string) => void;
  onToggleFileType: (mime: string, checked: boolean) => void;
}) {
  const fileTypeFilter = filters.find((filter) => filter.type === "fileType");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Filter"
          title="Filter"
          className="relative size-7 sm:size-7"
        >
          <HugeiconsIcon icon={FilterIcon} className="size-4" />
          {filters.length > 0 ? (
            <span className="absolute right-1 top-1 size-1.5 rounded-full bg-primary" />
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-44">
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <HugeiconsIcon
              icon={File01Icon}
              className="size-4 text-muted-foreground"
            />
            File type
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-60">
            <FileSystemFileTypeCommand
              checkedMimes={fileTypeFilter?.value ?? []}
              onToggle={onToggleFileType}
              options={fileTypeOptions}
            />
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        {(["dateModified", "dateCreated"] as const).map((type) => (
          <DropdownMenuSub key={type}>
            <DropdownMenuSubTrigger>
              <HugeiconsIcon
                icon={Calendar03Icon}
                className="size-4 text-muted-foreground"
              />
              {FILTER_TYPE_LABELS[type]}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <ScrollArea orientation="vertical" className="h-auto max-h-72">
                {DATE_FILTER_PRESETS.map((preset) => (
                  <DropdownMenuItem
                    key={preset}
                    onClick={() => onSelectDatePreset(type, preset)}
                  >
                    {preset}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem onClick={() => onOpenCustomRange(type)}>
                  Custom date range…
                </DropdownMenuItem>
              </ScrollArea>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const FILTER_PILL_SEGMENT_CLASSNAME =
  "flex h-5 items-center gap-1 border border-l-0 bg-background px-1.5 whitespace-nowrap text-foreground";

const FILTER_PILL_BUTTON_CLASSNAME = cn(
  FILTER_PILL_SEGMENT_CLASSNAME,
  "outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring",
);

// One applied filter, rendered as a segmented pill in the status bar:
// type · operator · value · remove, each segment interactive like Extend's
// table filter pills.
function FileSystemFilterPill({
  fileTypeOptions,
  filter,
  onOpenCustomRange,
  onOperatorChange,
  onRemove,
  onSelectDatePreset,
  onToggleFileType,
}: {
  fileTypeOptions: FileTypeFilterOption[];
  filter: FileSystemFilter;
  onOpenCustomRange?: () => void;
  onOperatorChange: (operator: FileSystemFilterOperator) => void;
  onRemove: () => void;
  onSelectDatePreset: (preset: string) => void;
  onToggleFileType: (mime: string, checked: boolean) => void;
}) {
  const isCustomRange =
    filter.type !== "fileType" && isCustomDateRangeValue(filter.value);
  const selectedTypeLabels =
    filter.type === "fileType"
      ? filter.value.map(
          (mime) =>
            fileTypeOptions.find((option) => option.mime === mime)?.label ??
            mime,
        )
      : [];

  return (
    <div className="flex items-center text-xs">
      <span
        className={cn(
          FILTER_PILL_SEGMENT_CLASSNAME,
          "rounded-l-md border-l text-primary",
        )}
      >
        <HugeiconsIcon
          icon={filter.type === "fileType" ? File01Icon : Calendar03Icon}
          className="size-3"
        />
        {FILTER_TYPE_LABELS[filter.type]}
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(FILTER_PILL_BUTTON_CLASSNAME, "text-primary")}
          >
            {FILTER_OPERATOR_LABELS[filter.operator]}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-28">
          {filterOperatorChoices(filter).map((operator) => (
            <DropdownMenuItem
              key={operator}
              onClick={() => onOperatorChange(operator)}
            >
              {FILTER_OPERATOR_LABELS[operator]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {filter.type === "fileType" ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              title={selectedTypeLabels.join(", ")}
              className={FILTER_PILL_BUTTON_CLASSNAME}
            >
              {filter.value.length === 1
                ? selectedTypeLabels[0]
                : `${filter.value.length} selected`}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-60">
            <FileSystemFileTypeCommand
              checkedMimes={filter.value}
              onToggle={onToggleFileType}
              options={fileTypeOptions}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      ) : isCustomRange ? (
        <button
          type="button"
          onClick={onOpenCustomRange}
          className={FILTER_PILL_BUTTON_CLASSNAME}
        >
          {filter.value
            .map((value) => new Date(value).toLocaleDateString("en-US"))
            .join(" – ")}
        </button>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className={FILTER_PILL_BUTTON_CLASSNAME}>
              {filter.value[0]}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <ScrollArea orientation="vertical" className="h-auto max-h-72">
              {DATE_FILTER_PRESETS.map((preset) => (
                <DropdownMenuItem
                  key={preset}
                  onClick={() => onSelectDatePreset(preset)}
                >
                  {preset}
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem onClick={onOpenCustomRange}>
                Custom date range…
              </DropdownMenuItem>
            </ScrollArea>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <button
        type="button"
        aria-label={`Remove ${FILTER_TYPE_LABELS[filter.type]} filter`}
        onClick={onRemove}
        className={cn(
          FILTER_PILL_BUTTON_CLASSNAME,
          "rounded-r-md px-1 text-muted-foreground hover:text-foreground",
        )}
      >
        <HugeiconsIcon icon={Cancel01Icon} className="size-3" />
      </button>
    </div>
  );
}

function formatDateInputValue(date: Date | undefined) {
  if (!date) return "";

  const pad = (value: number) => String(value).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function parseDateInputValue(value: string) {
  const trimmed = value.trim();

  if (!trimmed) return undefined;

  const isoMatch = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(trimmed);

  if (isoMatch) {
    const date = new Date(
      Number(isoMatch[1]),
      Number(isoMatch[2]) - 1,
      Number(isoMatch[3]),
    );

    return Number.isNaN(date.getTime()) ? undefined : date;
  }

  const parsed = Date.parse(trimmed);

  return Number.isNaN(parsed) ? undefined : new Date(parsed);
}

const DATE_RANGE_DIALOG_PRESETS = [
  "Last 7 days",
  "This month",
  "Last 1 month",
  "Last 3 months",
  "This year",
  "Last 12 months",
];

function dateRangePresetRange(preset: string) {
  const from = new Date();
  const to = new Date();

  from.setHours(0, 0, 0, 0);
  to.setHours(23, 59, 59, 999);

  switch (preset) {
    case "Last 7 days":
      from.setDate(from.getDate() - 6);
      break;
    case "This month":
      from.setDate(1);
      break;
    case "Last 1 month":
      from.setMonth(from.getMonth() - 1);
      break;
    case "Last 3 months":
      from.setMonth(from.getMonth() - 3);
      break;
    case "This year":
      from.setMonth(0, 1);
      break;
    case "Last 12 months":
      from.setFullYear(from.getFullYear() - 1);
      break;
  }
  return { from, to };
}

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function calendarDayKey(date: Date) {
  return date.getFullYear() * 10_000 + date.getMonth() * 100 + date.getDate();
}

// Two-month range calendar for the custom date range dialog (one month at
// phone widths). Clicking sets the start, then the end; clicking before the
// start swaps the ends, and a third click restarts the range.
function FileSystemRangeCalendar({
  onSelect,
  range,
}: {
  onSelect: (range: { from?: Date; to?: Date }) => void;
  range: { from?: Date; to?: Date };
}) {
  const [viewMonth, setViewMonth] = React.useState(() => {
    const base = range.from ?? new Date();

    return new Date(base.getFullYear(), base.getMonth(), 1);
  });
  const months = [
    viewMonth,
    new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1),
  ];
  const fromKey = range.from ? calendarDayKey(range.from) : null;
  const toKey = range.to ? calendarDayKey(range.to) : null;
  const todayKey = calendarDayKey(new Date());

  const handleDayClick = (day: Date) => {
    if (!range.from || range.to) {
      onSelect({ from: day });
    } else if (calendarDayKey(day) < calendarDayKey(range.from)) {
      onSelect({ from: day, to: range.from });
    } else {
      onSelect({ from: range.from, to: day });
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        aria-label="Previous month"
        onClick={() =>
          setViewMonth(
            (previous) =>
              new Date(previous.getFullYear(), previous.getMonth() - 1, 1),
          )
        }
        className="absolute left-0 top-0 flex size-6 items-center justify-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
      >
        <HugeiconsIcon icon={ArrowLeft01Icon} className="size-4" />
      </button>
      <button
        type="button"
        aria-label="Next month"
        onClick={() =>
          setViewMonth(
            (previous) =>
              new Date(previous.getFullYear(), previous.getMonth() + 1, 1),
          )
        }
        className="absolute right-0 top-0 flex size-6 items-center justify-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
      >
        <HugeiconsIcon icon={ArrowRight01Icon} className="size-4" />
      </button>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {months.map((month, monthIndex) => {
          const firstWeekday = month.getDay();
          const dayCount = new Date(
            month.getFullYear(),
            month.getMonth() + 1,
            0,
          ).getDate();
          const cells = [
            ...Array.from({ length: firstWeekday }, () => null),
            ...Array.from(
              { length: dayCount },
              (_, index) =>
                new Date(month.getFullYear(), month.getMonth(), index + 1),
            ),
          ];

          return (
            <div
              key={`${month.getFullYear()}-${month.getMonth()}`}
              className={cn(monthIndex === 1 && "max-sm:hidden")}
            >
              <div className="text-center text-sm font-medium leading-6">
                {month.toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </div>
              <div className="mt-1 grid grid-cols-7 text-center text-xs text-muted-foreground">
                {WEEKDAY_LABELS.map((weekday) => (
                  <span key={weekday} className="h-6 leading-6">
                    {weekday}
                  </span>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-y-px">
                {cells.map((day, cellIndex) => {
                  if (!day) return <span key={cellIndex} />;

                  const dayKey = calendarDayKey(day);
                  const isFrom = dayKey === fromKey;
                  const isTo = dayKey === toKey;
                  const isWithinRange =
                    fromKey !== null &&
                    toKey !== null &&
                    dayKey > fromKey &&
                    dayKey < toKey;

                  return (
                    <button
                      key={cellIndex}
                      type="button"
                      onClick={() => handleDayClick(day)}
                      className={cn(
                        "flex h-7 items-center justify-center rounded-md text-xs tabular-nums outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring",
                        isWithinRange && "rounded-none bg-accent",
                        (isFrom || isTo) &&
                          "bg-primary text-primary-foreground hover:bg-primary",
                        isFrom &&
                          toKey !== null &&
                          fromKey !== toKey &&
                          "rounded-r-none",
                        isTo && fromKey !== toKey && "rounded-l-none",
                        dayKey === todayKey &&
                          !isFrom &&
                          !isTo &&
                          "font-semibold text-primary",
                      )}
                    >
                      {day.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Custom date range dialog mirroring Extend's table filters: From/To inputs,
// a two-month range calendar, and quick presets. Applied ranges span from
// the start of the first day to the end of the last.
function FileSystemDateRangeDialog({
  initialRange,
  onApply,
  onClose,
}: {
  initialRange?: { from: Date; to: Date };
  onApply: (from: Date, to: Date) => void;
  onClose: () => void;
}) {
  const [range, setRange] = React.useState<{ from?: Date; to?: Date }>(
    () => initialRange ?? {},
  );
  const [fromInput, setFromInput] = React.useState(() =>
    formatDateInputValue(initialRange?.from),
  );
  const [toInput, setToInput] = React.useState(() =>
    formatDateInputValue(initialRange?.to),
  );

  const selectRange = (next: { from?: Date; to?: Date }) => {
    setRange(next);
    if (next.from) setFromInput(formatDateInputValue(next.from));
    if (next.to) setToInput(formatDateInputValue(next.to));
  };

  const dateField = (
    label: string,
    value: string,
    onChange: (value: string) => void,
  ) => (
    <div className="flex flex-1 flex-col gap-1.5">
      <span className="text-xs font-medium">{label}</span>
      <div className="relative flex items-center">
        <HugeiconsIcon
          icon={Calendar03Icon}
          className="pointer-events-none absolute left-2.5 size-3.5 text-muted-foreground"
        />
        <Input
          type="text"
          value={value}
          placeholder="YYYY-MM-DD"
          aria-label={`${label} date`}
          onChange={(event) => onChange(event.target.value)}
          className="h-8 pl-8 sm:h-8"
        />
      </div>
    </div>
  );

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="w-[30rem] max-w-[calc(100vw-2rem)]">
        <DialogHeader>
          <DialogTitle>Custom date range</DialogTitle>
        </DialogHeader>
        <DialogPanel className="flex flex-col gap-4">
          <div className="flex gap-3">
            {dateField("From", fromInput, (value) => {
              setFromInput(value);

              const parsed = parseDateInputValue(value);

              if (parsed)
                setRange((previous) => ({ ...previous, from: parsed }));
            })}
            {dateField("To", toInput, (value) => {
              setToInput(value);

              const parsed = parseDateInputValue(value);

              if (parsed) setRange((previous) => ({ ...previous, to: parsed }));
            })}
          </div>
          <FileSystemRangeCalendar range={range} onSelect={selectRange} />
          <div className="grid grid-cols-3 gap-2">
            {DATE_RANGE_DIALOG_PRESETS.map((preset) => (
              <Button
                key={preset}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => selectRange(dateRangePresetRange(preset))}
              >
                {preset}
              </Button>
            ))}
          </div>
        </DialogPanel>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!range.from || !range.to}
            onClick={() => {
              if (!range.from || !range.to) return;

              const from = new Date(range.from);
              const to = new Date(range.to);

              from.setHours(0, 0, 0, 0);
              to.setHours(23, 59, 59, 999);
              onApply(from, to);
            }}
          >
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type FileSystemViewProps = {
  currentPath: string;
  entries: FileSystemEntry[];
  fileFilter: ((file: FileEntry) => boolean) | null;
  getFileUrl?: (file: FileSystemFileItem) => string | Promise<string>;
  index: FileSystemIndex;
  loadPreviewImageUrl?: (
    file: FileSystemFileItem,
    pageIndex: number,
  ) => Promise<string | null>;
  loadingFolders: Set<string>;
  onOpen: (entry: FileSystemEntry) => void;
  onSelect: (entry: FileSystemEntry | null) => void;
  onSortColumnClick: (key: FileSystemSortKey) => void;
  /** Pooled paths currently attached to the DOM (reveal instantly). */
  attachedStagePaths: string[];
  /** `"path#pageIndex"` → thumbnail URL, shared by every pager. */
  pageUrlCache: Map<string, string>;
  /** Admits a file into the root-owned keep-alive preview pool. */
  poolStagePath: (path: string) => void;
  /** Mounts/unmounts the gallery host element for a pooled path. */
  registerStageHost: (path: string, element: HTMLElement | null) => void;
  renderFilePreview?: (file: FileSystemFileItem) => React.ReactNode;
  searchQuery: string;
  selectedEntry: FileSystemEntry | null;
  selectedPath: string | null;
  sort: FileSystemSortState;
  /** Expanded tree folders per folder path, surviving view switches. */
  treeExpansionRef: React.MutableRefObject<Map<string, readonly string[]>>;
  // Morgan extension fields (see FileSystemProps for the public surface).
  /** OS file drops on folder rows are accepted. */
  acceptsExternalDrop: boolean;
  /** Rows/tiles are draggable. */
  canDragItems: boolean;
  /** An item context menu is supplied (and not read-only). */
  contextMenuEnabled: boolean;
  itemColumnsHeader?: React.ReactNode;
  /** Routes a folder drop (internal or OS files) to the right callback. */
  onFolderDrop: (folderPath: string, dataTransfer: DataTransfer) => void;
  /** Marks the dragged selection on the dataTransfer + internal ref. */
  onItemDragStart: (entry: FileSystemEntry, dataTransfer: DataTransfer) => void;
  /** Direct move used by the tree view's native drag sessions. */
  onMoveEntries: (
    entries: readonly FileSystemEntry[],
    targetFolderPath: string,
  ) => void;
  onRangeSelect: (
    entry: FileSystemEntry,
    orderedEntries: readonly FileSystemEntry[],
  ) => void;
  onSelectMany: (entries: readonly FileSystemEntry[]) => void;
  onToggleSelect: (entry: FileSystemEntry) => void;
  renderItemColumns?: (item: FileSystemItem) => React.ReactNode;
  getItemClassName?: (item: FileSystemItem) => string | undefined;
  renderPreviewDetails?: (file: FileSystemFileItem) => React.ReactNode;
  renderLargePreview?: (file: FileSystemFileItem) => React.ReactNode;
  selectedPathsSet: ReadonlySet<string>;
};

// Resolves a display URL for a file: its own `url`, else via `getFileUrl`.
// Keyed by path/url (not object identity) so manifest churn — e.g. thumbnails
// streaming in — doesn't re-trigger presign calls for the same file. An
// optional `cache` shared across mounts serves revisited files synchronously:
// no repeat presign round-trip (and no loading flash), and the stable URL
// keeps the browser's HTTP cache valid for already-fetched content.
function useResolvedFileUrl(
  file: FileEntry | null,
  getFileUrl?: (file: FileSystemFileItem) => string | Promise<string>,
  cache?: Map<string, string>,
) {
  const [state, setState] = React.useState<{
    isResolving: boolean;
    url: string | null;
  }>(() => ({
    isResolving: false,
    url: file ? (file.url ?? cache?.get(file.path) ?? null) : null,
  }));
  const fileRef = React.useRef(file);

  React.useEffect(() => {
    fileRef.current = file;
  });

  const filePath = file?.path ?? null;
  const fileUrl = file?.url ?? null;

  React.useEffect(() => {
    const currentFile = fileRef.current;
    const knownUrl =
      fileUrl ?? (filePath ? (cache?.get(filePath) ?? null) : null);

    if (!currentFile || knownUrl || !getFileUrl) {
      setState({ isResolving: false, url: knownUrl });
      return;
    }

    let isCurrent = true;

    setState({ isResolving: true, url: null });
    void Promise.resolve(getFileUrl(currentFile))
      .then((url) => {
        if (url) cache?.set(currentFile.path, url);
        if (isCurrent) setState({ isResolving: false, url });
      })
      .catch(() => {
        if (isCurrent) setState({ isResolving: false, url: null });
      });

    return () => {
      isCurrent = false;
    };
  }, [cache, filePath, fileUrl, getFileUrl]);

  return state;
}

// Returns `value` once it has stopped changing for `delay` ms. Gallery
// navigation scrubs past files quickly; heavy previews (document viewers,
// presigned URL resolution) only kick in for the file the user lands on.
function useSettledValue<T>(value: T, delay: number): T {
  const [settled, setSettled] = React.useState(value);

  React.useEffect(() => {
    if (Object.is(settled, value)) return;

    const timeout = window.setTimeout(() => setSettled(value), delay);

    return () => window.clearTimeout(timeout);
  }, [delay, settled, value]);

  return settled;
}

function FileSystemEmptyState({
  label,
  isLoading = false,
}: {
  label: string;
  isLoading?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex size-full items-center justify-center text-sm text-muted-foreground",
        isLoading && "animate-pulse motion-reduce:animate-none",
      )}
    >
      {label}
    </div>
  );
}

const ARROW_KEYS = new Set(["ArrowDown", "ArrowLeft", "ArrowRight", "ArrowUp"]);

// Type-ahead buffers reset after this idle period, like Finder.
const TYPE_AHEAD_RESET_MS = 700;

// Letters and digits only — the same key test the tree uses — so shortcuts
// and whitespace scrolling stay untouched.
function isTypeAheadKey(event: React.KeyboardEvent) {
  return (
    event.key.length === 1 &&
    /^[\p{L}\p{N}]$/u.test(event.key) &&
    !event.altKey &&
    !event.ctrlKey &&
    !event.metaKey
  );
}

// Shared Finder-style type-ahead used by every view: printable keys
// accumulate a buffer that jumps to the next entry whose name starts with
// it, and repeating a single letter cycles through entries with that
// prefix. Each view passes its own display-ordered candidate list, so the
// same keystrokes land on the same file everywhere.
function useEntryTypeAhead() {
  const stateRef = React.useRef({ buffer: "", timeout: 0 });

  React.useEffect(() => {
    const state = stateRef.current;

    return () => window.clearTimeout(state.timeout);
  }, []);

  return React.useCallback(
    (
      event: React.KeyboardEvent,
      entries: readonly FileSystemEntry[],
      currentIndex: number,
    ) => {
      if (!isTypeAheadKey(event) || entries.length === 0) return null;

      // Embedded viewers (and any future inputs) keep their keystrokes.
      const target = event.target;

      if (
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT")
      ) {
        return null;
      }

      const state = stateRef.current;

      window.clearTimeout(state.timeout);
      state.timeout = window.setTimeout(() => {
        state.buffer = "";
      }, TYPE_AHEAD_RESET_MS);
      state.buffer += event.key.toLowerCase();

      // A repeated single letter advances past the current entry; a longer
      // buffer refines the match in place.
      const startIndex =
        currentIndex < 0
          ? 0
          : currentIndex + (state.buffer.length === 1 ? 1 : 0);

      for (let step = 0; step < entries.length; step += 1) {
        const entry = entries[(startIndex + step) % entries.length];

        if (entry.name.toLowerCase().startsWith(state.buffer)) {
          event.preventDefault();
          return entry;
        }
      }
      event.preventDefault();
      return null;
    },
    [],
  );
}

// Selects (and focuses) the entry reached by an arrow key. Up/down use row
// geometry so navigation follows the rendered auto-fill grid.
function moveGridSelection({
  entries,
  itemRefs,
  key,
  onSelect,
  selectedPath,
}: {
  entries: FileSystemEntry[];
  itemRefs: Map<string, HTMLButtonElement>;
  key: string;
  onSelect: (entry: FileSystemEntry | null) => void;
  selectedPath: string | null;
}) {
  if (entries.length === 0) return false;

  const currentIndex = entries.findIndex(
    (entry) => entry.path === selectedPath,
  );
  let nextEntry: FileSystemEntry | undefined;

  if (currentIndex === -1) {
    nextEntry = entries[0];
  } else if (key === "ArrowLeft" || key === "ArrowRight") {
    nextEntry = entries[currentIndex + (key === "ArrowLeft" ? -1 : 1)];
  } else {
    const currentElement = itemRefs.get(entries[currentIndex].path);

    if (!currentElement) return false;

    const currentRect = currentElement.getBoundingClientRect();
    let bestScore = Infinity;

    for (const entry of entries) {
      if (entry.path === selectedPath) continue;

      const rect = itemRefs.get(entry.path)?.getBoundingClientRect();

      if (!rect) continue;

      const rowDelta =
        key === "ArrowDown"
          ? rect.top - currentRect.top
          : currentRect.top - rect.top;

      if (rowDelta <= 1) continue;

      const score = rowDelta * 1000 + Math.abs(rect.left - currentRect.left);

      if (score < bestScore) {
        bestScore = score;
        nextEntry = entry;
      }
    }
  }

  if (!nextEntry) return false;

  onSelect(nextEntry);
  itemRefs.get(nextEntry.path)?.focus();
  return true;
}

// Icon grid geometry (px at the default 16px root font size). Tiles have a
// fixed height — a 4rem glyph box plus a reserved two-line label — so rows
// share one stride and the grid can window cleanly.
const ICON_GRID_PADDING = 12; // p-3
const ICON_MIN_TILE_WIDTH = 104; // 6.5rem
const ICON_TILE_GAP_X = 4; // gap-x-1
const ICON_TILE_HEIGHT = 102; // h-16 glyph box + gap-1.5 + two text-xs lines
const ICON_ROW_GAP = 12; // gap-y-3
const ICON_ROW_STRIDE = ICON_TILE_HEIGHT + ICON_ROW_GAP;

function FileSystemIconsView({
  acceptsExternalDrop,
  canDragItems,
  entries,
  loadPreviewImageUrl,
  onFolderDrop,
  onItemDragStart,
  onOpen,
  onRangeSelect,
  onSelect,
  onToggleSelect,
  pageUrlCache,
  renderFilePreview,
  selectedPath,
  selectedPathsSet,
}: FileSystemViewProps) {
  const itemRefs = React.useRef(new Map<string, HTMLButtonElement>());
  const viewportRef = React.useRef<HTMLDivElement | null>(null);
  const typeAhead = useEntryTypeAhead();
  // Morgan extension: folder tile currently highlighted as a drop target.
  const [dragOverPath, setDragOverPath] = React.useState<string | null>(null);
  const acceptsDropOn = (entry: FileSystemEntry, dataTransfer: DataTransfer) =>
    entry.kind === "folder" &&
    (dataTransfer.types.includes(MORGAN_ITEMS_DRAG_TYPE)
      ? canDragItems
      : acceptsExternalDrop && dataTransfer.types.includes("Files"));
  // The column count mirrors what `repeat(auto-fill, minmax(6.5rem, 1fr))`
  // produces (the CSS owns the actual layout) so item indices map to grid
  // rows — the windowing below depends on that mapping. It stays null until
  // the first client measure; server markup must not guess.
  const [columnCount, setColumnCount] = React.useState<number | null>(null);

  React.useLayoutEffect(() => {
    const viewport = viewportRef.current;

    if (!viewport || typeof ResizeObserver === "undefined") return;

    const update = () => {
      const available = viewport.clientWidth - ICON_GRID_PADDING * 2;

      setColumnCount(
        Math.max(
          1,
          Math.floor(
            (available + ICON_TILE_GAP_X) /
              (ICON_MIN_TILE_WIDTH + ICON_TILE_GAP_X),
          ),
        ),
      );
    };
    const observer = new ResizeObserver(update);

    update();
    observer.observe(viewport);
    return () => observer.disconnect();
  }, []);

  const resolvedColumnCount = columnCount ?? 1;
  const rowCount = Math.ceil(entries.length / resolvedColumnCount);
  const { end, start } = useVirtualWindow({
    count: rowCount,
    itemStride: ICON_ROW_STRIDE,
    leadingPx: ICON_GRID_PADDING,
    overscan: 4,
    viewportRef,
  });
  const visibleEntries = entries.slice(
    start * resolvedColumnCount,
    end * resolvedColumnCount,
  );

  // Selection can land outside the window (view switches, shrinking results);
  // bring its row back into the viewport so the tile mounts and is focusable.
  React.useLayoutEffect(() => {
    if (!selectedPath) return;

    const entryIndex = entries.findIndex(
      (entry) => entry.path === selectedPath,
    );

    if (entryIndex === -1) return;

    scrollIndexIntoView({
      index: Math.floor(entryIndex / resolvedColumnCount),
      itemSize: ICON_TILE_HEIGHT,
      itemStride: ICON_ROW_STRIDE,
      leadingPx: ICON_GRID_PADDING,
      viewport: viewportRef.current,
    });
  }, [entries, resolvedColumnCount, selectedPath]);

  // Roving tabindex: the grid is a single tab stop (the selected tile when
  // rendered, else the first rendered one), so Shift+Tab returns to the
  // toolbar like in the list view.
  const tabStopPath = visibleEntries.some(
    (entry) => entry.path === selectedPath,
  )
    ? selectedPath
    : (visibleEntries[0]?.path ?? null);

  return (
    <ScrollArea
      orientation="vertical"
      viewportRef={viewportRef}
      viewportClassName="p-3"
      viewportProps={{
        onClick: (event) => {
          if (event.target === event.currentTarget) onSelect(null);
        },
      }}
    >
      <div
        className="relative"
        // The scroll-height spacer needs the measured column count; until
        // then the absolutely positioned grid alone defines the scrollable
        // overflow, so the server-rendered frame doesn't flash an
        // oversized scroll range.
        style={{
          height:
            columnCount !== null && rowCount
              ? rowCount * ICON_ROW_STRIDE - ICON_ROW_GAP
              : undefined,
        }}
      >
        <div
          role="listbox"
          aria-label="Files"
          className="absolute inset-x-0 grid gap-x-1 gap-y-3"
          // The auto-fill expression produces the same column count the
          // ResizeObserver measures (the measurement exists only for the
          // windowing math), so the server-rendered first paint is already
          // a grid instead of flashing a single stacked column until the
          // first client measure.
          style={{
            gridTemplateColumns: "repeat(auto-fill, minmax(6.5rem, 1fr))",
            top: start * ICON_ROW_STRIDE,
          }}
          onKeyDown={(event) => {
            if (!ARROW_KEYS.has(event.key)) {
              const match = typeAhead(
                event,
                entries,
                entries.findIndex((entry) => entry.path === selectedPath),
              );

              if (match) {
                onSelect(match);
                // The matched tile may be outside the virtual window; the
                // selection effect scrolls it in, and focus follows once it
                // has mounted.
                requestAnimationFrame(() =>
                  itemRefs.current.get(match.path)?.focus(),
                );
              }
              return;
            }
            if (
              moveGridSelection({
                entries,
                itemRefs: itemRefs.current,
                key: event.key,
                onSelect,
                selectedPath,
              })
            ) {
              event.preventDefault();
            }
          }}
        >
          {visibleEntries.map((entry) => {
            const isSelected =
              selectedPathsSet.has(entry.path) || entry.path === selectedPath;
            const isDropTarget = entry.path === dragOverPath;

            return (
              <button
                key={entry.path}
                type="button"
                role="option"
                aria-selected={isSelected}
                // Morgan extension: lets the context-menu wrapper resolve
                // the right-clicked tile.
                data-file-system-path={entry.path}
                tabIndex={entry.path === tabStopPath ? 0 : -1}
                ref={(element) => {
                  if (element) {
                    itemRefs.current.set(entry.path, element);
                  } else {
                    itemRefs.current.delete(entry.path);
                  }
                }}
                // Morgan extension: drag source + folder drop target.
                draggable={canDragItems || undefined}
                onDragStart={
                  canDragItems
                    ? (event) => onItemDragStart(entry, event.dataTransfer)
                    : undefined
                }
                onDragOver={(event) => {
                  if (!acceptsDropOn(entry, event.dataTransfer)) return;
                  event.preventDefault();
                  setDragOverPath(entry.path);
                }}
                onDragLeave={() => {
                  setDragOverPath((previous) =>
                    previous === entry.path ? null : previous,
                  );
                }}
                onDrop={(event) => {
                  if (!acceptsDropOn(entry, event.dataTransfer)) return;
                  event.preventDefault();
                  event.stopPropagation();
                  setDragOverPath(null);
                  onFolderDrop(entry.path, event.dataTransfer);
                }}
                onClick={(event) => {
                  // Morgan extension: cmd/ctrl toggles, shift extends.
                  if (event.metaKey || event.ctrlKey) {
                    onToggleSelect(entry);
                  } else if (event.shiftKey) {
                    onRangeSelect(entry, entries);
                  } else {
                    onSelect(entry);
                  }
                }}
                onDoubleClick={() => onOpen(entry)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") onOpen(entry);
                }}
                className="group flex h-[6.375rem] flex-col items-center gap-1.5 outline-none"
              >
                <span
                  className={cn(
                    "flex h-16 w-20 shrink-0 items-center justify-center rounded-lg p-1 transition-colors group-focus-visible:ring-2 group-focus-visible:ring-ring",
                    isSelected && "bg-accent",
                    isDropTarget && "bg-accent ring-2 ring-ring",
                  )}
                >
                  {entry.kind === "folder" ? (
                    <FileSystemFolderGlyph className="h-13 w-auto drop-shadow-sm" />
                  ) : (
                    <FileVisual
                      file={entry}
                      className={cn(
                        "rounded-sm shadow-sm",
                        // Landscape thumbnails get extra width so they fill
                        // the tile instead of rendering as a short sliver.
                        (entry.previewAspectRatio ?? 0.78) > 1.2
                          ? "w-[4.75rem]"
                          : "w-12",
                      )}
                      // Morgan extension: tiles load lazy page-1 previews
                      // (e.g. PDFs) like the columns/gallery stages do. With
                      // a single known page the hover pager never shows.
                      loadPreviewImageUrl={loadPreviewImageUrl}
                      pageable
                      pageUrlCache={pageUrlCache}
                      previewAspectRatio={0.78}
                      renderFilePreview={renderFilePreview}
                    />
                  )}
                </span>
                <span
                  className={cn(
                    "max-w-full break-words rounded-sm px-1.5 py-px text-center text-xs leading-tight",
                    isSelected
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground",
                  )}
                >
                  <span className="line-clamp-2">{entry.name}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </ScrollArea>
  );
}

// One sortable column header for the list view; the active column shows the
// direction chevron on its right.
function FileSystemListColumnHeader({
  className,
  label,
  onClick,
  sort,
  sortKey,
}: {
  className?: string;
  label: string;
  onClick: (key: FileSystemSortKey) => void;
  sort: FileSystemSortState;
  sortKey: FileSystemSortKey;
}) {
  const isActive = sort.key === sortKey;

  return (
    <button
      type="button"
      onClick={() => onClick(sortKey)}
      className={cn(
        "flex items-center gap-0.5 rounded-sm py-0.5 outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring",
        isActive && "text-foreground",
        className,
      )}
    >
      {label}
      {isActive ? (
        <HugeiconsIcon
          icon={sort.direction === "asc" ? ArrowUp01Icon : ArrowDown01Icon}
          className="size-3 shrink-0"
        />
      ) : null}
    </button>
  );
}

function FileSystemListView(props: FileSystemViewProps) {
  const {
    currentPath,
    fileFilter,
    index,
    itemColumnsHeader,
    onOpen,
    onSelectMany,
    onSortColumnClick,
    renderItemColumns,
    searchQuery,
    selectedPath,
    sort,
    treeExpansionRef,
  } = props;
  // Morgan extension: extra item columns cannot render inside the
  // @pierre/trees shadow DOM, so supplying them switches the list view to a
  // flat, React-owned row list over the current folder (no inline folder
  // disclosure; folders open on double-click like the icons view).
  const isExtended =
    renderItemColumns !== undefined || itemColumnsHeader !== undefined;

  // Filters narrow the path list handed to the tree; the search query stays
  // out of it so the tree's own search session (with match highlighting)
  // keeps handling it without remounts per keystroke.
  const relativePaths = React.useMemo(() => {
    if (isExtended) return [];

    const paths: string[] = [];

    for (const [path, file] of index.files) {
      if (currentPath === "" || path.startsWith(currentPath)) {
        const relativePath = path.slice(currentPath.length);

        if (!relativePath) continue;
        if (fileFilter && !fileFilter(file)) continue;
        paths.push(relativePath);
      }
    }
    return paths.sort();
  }, [currentPath, fileFilter, index, isExtended]);

  if (!isExtended && relativePaths.length === 0) {
    return (
      <FileSystemEmptyState
        label={
          fileFilter
            ? "No items match the active filters"
            : "This folder is empty"
        }
      />
    );
  }

  if (isExtended) {
    return (
      <div className="flex size-full flex-col">
        {/* Extended header: paddings/gaps mirror the extended rows (4px
            viewport padding + px-2 row padding + gap-2 + 16px icon lane)
            so the columns line up. */}
        <div className="flex shrink-0 items-center gap-2 border-b py-1 pl-3 pr-4 text-xs font-medium text-muted-foreground">
          <span aria-hidden className="w-4 shrink-0" />
          <FileSystemListColumnHeader
            className="min-w-0 flex-1 justify-start"
            label="Name"
            onClick={onSortColumnClick}
            sort={sort}
            sortKey="name"
          />
          {itemColumnsHeader}
          <FileSystemListColumnHeader
            className="w-36 shrink-0 justify-end"
            label="Date"
            onClick={onSortColumnClick}
            sort={sort}
            sortKey="createdAt"
          />
          <FileSystemListColumnHeader
            className="w-16 shrink-0 justify-end"
            label="Size"
            onClick={onSortColumnClick}
            sort={sort}
            sortKey="size"
          />
        </div>
        <FileSystemExtendedListView {...props} />
      </div>
    );
  }

  return (
    <div className="flex size-full flex-col">
      {/* Paddings match the tree's row geometry: name text starts 46px in
          (16px tree padding + 30px icon lane), metadata ends 24px from the
          right (16px tree padding + 8px decoration inset). */}
      <div className="flex shrink-0 items-center border-b py-1 pl-[46px] pr-6 text-xs font-medium text-muted-foreground">
        <FileSystemListColumnHeader
          className="flex-1 justify-start"
          label="Name"
          onClick={onSortColumnClick}
          sort={sort}
          sortKey="name"
        />
        <FileSystemListColumnHeader
          className="w-44 justify-end"
          label="Date Modified"
          onClick={onSortColumnClick}
          sort={sort}
          sortKey="updatedAt"
        />
        <FileSystemListColumnHeader
          className="w-20 justify-end"
          label="Size"
          onClick={onSortColumnClick}
          sort={sort}
          sortKey="size"
        />
      </div>
      {/* Keyed by folder only: navigation remounts the tree, while filter,
          sort, and manifest changes update the mounted model in place so
          folder disclosure state survives them. */}
      <FileSystemPierreTree
        key={currentPath}
        acceptsExternalDrop={props.acceptsExternalDrop}
        contextMenuEnabled={props.contextMenuEnabled}
        currentPath={currentPath}
        dragEnabled={props.canDragItems}
        hasActiveFilters={fileFilter !== null}
        index={index}
        initialSelectedPath={
          selectedPath?.startsWith(currentPath)
            ? selectedPath.slice(currentPath.length).replace(/\/$/, "")
            : null
        }
        onExternalFolderDrop={props.onFolderDrop}
        onMoveEntries={props.onMoveEntries}
        onOpen={onOpen}
        onSelectMany={onSelectMany}
        relativePaths={relativePaths}
        searchQuery={searchQuery}
        sort={sort}
        treeExpansionRef={treeExpansionRef}
      />
    </div>
  );
}

// Morgan extension: row geometry for the extended list view.
const EXTENDED_LIST_ROW_HEIGHT = 36;

// Morgan extension: flat React-owned list rows for the current folder,
// rendered when `renderItemColumns`/`itemColumnsHeader` are supplied. Rows
// are divs (role=option) rather than buttons so the extra cells can host
// interactive controls (selects, badges with click handlers).
function FileSystemExtendedListView({
  acceptsExternalDrop,
  canDragItems,
  entries,
  index,
  onFolderDrop,
  onItemDragStart,
  onOpen,
  onRangeSelect,
  onSelect,
  onToggleSelect,
  renderFilePreview,
  renderItemColumns,
  getItemClassName,
  selectedPath,
  selectedPathsSet,
}: FileSystemViewProps) {
  void renderFilePreview;

  const rowRefs = React.useRef(new Map<string, HTMLDivElement>());
  const viewportRef = React.useRef<HTMLDivElement | null>(null);
  const typeAhead = useEntryTypeAhead();
  const [dragOverPath, setDragOverPath] = React.useState<string | null>(null);

  const { end, start } = useVirtualWindow({
    count: entries.length,
    itemStride: EXTENDED_LIST_ROW_HEIGHT,
    leadingPx: 4,
    overscan: 12,
    viewportRef,
  });

  // Keep the selection mounted/visible across keyboard moves and external
  // selection changes (same approach as the icons view).
  React.useLayoutEffect(() => {
    if (!selectedPath) return;

    scrollIndexIntoView({
      index: entries.findIndex((entry) => entry.path === selectedPath),
      itemSize: EXTENDED_LIST_ROW_HEIGHT,
      itemStride: EXTENDED_LIST_ROW_HEIGHT,
      leadingPx: 4,
      viewport: viewportRef.current,
    });
  }, [entries, selectedPath]);

  const acceptsDropOn = (entry: FileSystemEntry, dataTransfer: DataTransfer) =>
    entry.kind === "folder" &&
    (dataTransfer.types.includes(MORGAN_ITEMS_DRAG_TYPE)
      ? canDragItems
      : acceptsExternalDrop && dataTransfer.types.includes("Files"));

  const visibleEntries = entries.slice(start, end);
  const tabStopPath = visibleEntries.some(
    (entry) => entry.path === selectedPath,
  )
    ? selectedPath
    : (visibleEntries[0]?.path ?? null);

  const focusRow = (path: string) => {
    requestAnimationFrame(() => rowRefs.current.get(path)?.focus());
  };

  return (
    <ScrollArea
      orientation="vertical"
      className="min-h-0 flex-1"
      viewportRef={viewportRef}
      viewportClassName="p-1"
      viewportProps={{
        onClick: (event) => {
          if (event.target === event.currentTarget) onSelect(null);
        },
      }}
    >
      <div
        className="relative"
        style={{
          height: entries.length
            ? entries.length * EXTENDED_LIST_ROW_HEIGHT
            : undefined,
        }}
      >
        <div
          role="listbox"
          aria-label="Files"
          className="absolute inset-x-0 flex flex-col"
          style={{ top: start * EXTENDED_LIST_ROW_HEIGHT }}
          onKeyDown={(event) => {
            if (event.key === "ArrowUp" || event.key === "ArrowDown") {
              const currentIndex = entries.findIndex(
                (entry) => entry.path === selectedPath,
              );
              const nextEntry =
                currentIndex === -1
                  ? entries[0]
                  : entries[currentIndex + (event.key === "ArrowUp" ? -1 : 1)];

              if (nextEntry) {
                event.preventDefault();
                onSelect(nextEntry);
                focusRow(nextEntry.path);
              }
              return;
            }

            const match = typeAhead(
              event,
              entries,
              entries.findIndex((entry) => entry.path === selectedPath),
            );

            if (match) {
              onSelect(match);
              focusRow(match.path);
            }
          }}
        >
          {visibleEntries.map((entry) => {
            const isSelected =
              selectedPathsSet.has(entry.path) || entry.path === selectedPath;
            const isDropTarget = entry.path === dragOverPath;
            const dateText =
              formatTimestamp(entry.updatedAt ?? entry.createdAt) ?? "—";
            const trailingText =
              entry.kind === "file"
                ? (formatByteSize(entry.size) ?? "—")
                : (() => {
                    const childCount = index.children.get(entry.path)?.length;

                    return childCount === undefined
                      ? "—"
                      : `${childCount} ${childCount === 1 ? "item" : "items"}`;
                  })();

            return (
              <div
                key={entry.path}
                role="option"
                aria-selected={isSelected}
                // Selected rows sit on the primary surface — the opposite
                // of the mode's background — so the file-type icons and the
                // folder glyph swap to the opposite palette (same flip as
                // the columns view).
                data-file-system-on-primary={isSelected ? "" : undefined}
                data-file-system-path={entry.path}
                tabIndex={entry.path === tabStopPath ? 0 : -1}
                ref={(element) => {
                  if (element) {
                    rowRefs.current.set(entry.path, element);
                  } else {
                    rowRefs.current.delete(entry.path);
                  }
                }}
                draggable={canDragItems || undefined}
                onDragStart={
                  canDragItems
                    ? (event) => onItemDragStart(entry, event.dataTransfer)
                    : undefined
                }
                onDragOver={(event) => {
                  if (!acceptsDropOn(entry, event.dataTransfer)) return;
                  event.preventDefault();
                  setDragOverPath(entry.path);
                }}
                onDragLeave={() => {
                  setDragOverPath((previous) =>
                    previous === entry.path ? null : previous,
                  );
                }}
                onDrop={(event) => {
                  if (!acceptsDropOn(entry, event.dataTransfer)) return;
                  event.preventDefault();
                  event.stopPropagation();
                  setDragOverPath(null);
                  onFolderDrop(entry.path, event.dataTransfer);
                }}
                onClick={(event) => {
                  if (event.metaKey || event.ctrlKey) {
                    onToggleSelect(entry);
                  } else if (event.shiftKey) {
                    onRangeSelect(entry, entries);
                  } else {
                    onSelect(entry);
                  }
                }}
                onDoubleClick={() => onOpen(entry)}
                onKeyDown={(event) => {
                  if (
                    event.key === "Enter" &&
                    event.target === event.currentTarget
                  ) {
                    onOpen(entry);
                  }
                }}
                className={cn(
                  "flex h-9 shrink-0 cursor-default select-none items-center gap-2 rounded-md px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  // Selected rows sit on the brand green like the columns
                  // view; lighten any plain muted text inside (date, size,
                  // uploader, outline status badge) so it stays legible.
                  isSelected
                    ? "bg-primary text-primary-foreground [&_.text-muted-foreground]:text-primary-foreground/70"
                    : cn("hover:bg-accent/50", getItemClassName?.(entry)),
                  isDropTarget && "bg-accent ring-1 ring-ring",
                )}
              >
                {entry.kind === "folder" ? (
                  <FileSystemFolderGlyph className="h-3.5 w-4 shrink-0" />
                ) : (
                  <FileTypeIcon
                    fileName={entry.name}
                    className="size-4 shrink-0"
                  />
                )}
                <span className="min-w-0 flex-1 truncate" title={entry.name}>
                  {entry.name}
                </span>
                {renderItemColumns?.(entry)}
                <span className="w-36 shrink-0 truncate text-right text-xs text-muted-foreground">
                  {dateText}
                </span>
                <span className="w-16 shrink-0 whitespace-nowrap text-right text-xs text-muted-foreground">
                  {trailingText}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </ScrollArea>
  );
}

// Embedded thumbnail symbols grow the sprite injected into the tree's shadow
// DOM (data-URL covers can run hundreds of KB each); past this many the
// remaining files fall back to the built-in file-type icons alone.
const TREE_THUMBNAIL_SPRITE_LIMIT = 400;

function FileSystemPierreTree({
  acceptsExternalDrop,
  contextMenuEnabled,
  currentPath,
  dragEnabled,
  hasActiveFilters,
  index,
  initialSelectedPath,
  onExternalFolderDrop,
  onMoveEntries,
  onOpen,
  onSelectMany,
  relativePaths,
  searchQuery,
  sort,
  treeExpansionRef,
}: {
  acceptsExternalDrop: boolean;
  contextMenuEnabled: boolean;
  currentPath: string;
  dragEnabled: boolean;
  hasActiveFilters: boolean;
  index: FileSystemIndex;
  initialSelectedPath: string | null;
  onExternalFolderDrop: (
    folderPath: string,
    dataTransfer: DataTransfer,
  ) => void;
  onMoveEntries: (
    entries: readonly FileSystemEntry[],
    targetFolderPath: string,
  ) => void;
  onOpen: (entry: FileSystemEntry) => void;
  onSelectMany: (entries: readonly FileSystemEntry[]) => void;
  relativePaths: string[];
  searchQuery: string;
  sort: FileSystemSortState;
  treeExpansionRef: React.MutableRefObject<Map<string, readonly string[]>>;
}) {
  // The tree's comparator receives whole paths, not siblings, so it walks
  // the shared segments and applies the active sort at the first level the
  // two paths diverge — keeping directories first per level, the tree's
  // default convention. Lookups go through the index maps, which are stable
  // across search keystrokes.
  const indexFiles = index.files;
  const indexFolders = index.folders;
  const sortComparator = React.useMemo<
    "default" | FileTreeSortComparator
  >(() => {
    if (
      sort.key === DEFAULT_SORT.key &&
      sort.direction === DEFAULT_SORT.direction
    ) {
      return "default";
    }

    const entryAtDepth = (sortEntry: FileTreeSortEntry, depth: number) => {
      const isDirectory =
        depth < sortEntry.segments.length - 1 || sortEntry.isDirectory;
      const absolutePath = `${currentPath}${sortEntry.segments
        .slice(0, depth + 1)
        .join("/")}${isDirectory ? "/" : ""}`;

      return isDirectory
        ? indexFolders.get(absolutePath)
        : indexFiles.get(absolutePath);
    };

    return (left, right) => {
      const sharedDepth = Math.min(left.segments.length, right.segments.length);

      for (let depth = 0; depth < sharedDepth; depth += 1) {
        if (left.segments[depth] === right.segments[depth]) continue;

        const leftIsDirectory =
          depth < left.segments.length - 1 || left.isDirectory;
        const rightIsDirectory =
          depth < right.segments.length - 1 || right.isDirectory;

        if (leftIsDirectory !== rightIsDirectory) {
          return leftIsDirectory ? -1 : 1;
        }

        const leftEntry = entryAtDepth(left, depth);
        const rightEntry = entryAtDepth(right, depth);

        if (leftEntry && rightEntry) {
          return compareEntriesBySort(leftEntry, rightEntry, sort);
        }
        return left.segments[depth] < right.segments[depth] ? -1 : 1;
      }
      return left.segments.length - right.segments.length;
    };
  }, [currentPath, indexFiles, indexFolders, sort]);
  const preparedInput = React.useMemo(
    () => prepareFileTreeInput(relativePaths, { sort: sortComparator }),
    [relativePaths, sortComparator],
  );
  // Inject per-file thumbnails into the tree's shadow DOM as sprite symbols
  // wrapping an <image>, remapped onto rows by file basename. Files without
  // a thumbnail resolve through the built-in complete icon set instead — the
  // same colored file-type icons the other views use. The chevron is
  // remapped to the Hugeicons arrow so it matches the rest of the component;
  // the tree's rotation CSS keys off data-icon-name, which remapping keeps.
  const icons = React.useMemo(() => {
    const byFileName: Record<string, { name: string; viewBox: string }> = {};
    const symbols: string[] = [
      `<symbol id="file-system-chevron" viewBox="0 0 24 24"><path d="M18 9.00005C18 9.00005 13.5811 15 12 15C10.4188 15 6 9 6 9" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/></symbol>`,
    ];

    let thumbnailCount = 0;

    for (const relativePath of relativePaths) {
      if (thumbnailCount >= TREE_THUMBNAIL_SPRITE_LIMIT) break;

      const file = index.files.get(`${currentPath}${relativePath}`);
      const coverUrl = file ? filePreviewUrls(file)[0] : undefined;

      if (!file || !coverUrl) continue;

      const baseName = file.name.toLowerCase();

      if (byFileName[baseName]) continue;

      const symbolId = `file-system-thumbnail-${symbols.length}`;

      symbols.push(
        `<symbol id="${symbolId}" viewBox="0 0 16 16"><clipPath id="${symbolId}-clip"><rect width="16" height="16" rx="2.5"/></clipPath><image href="${escapeXmlAttribute(coverUrl)}" width="16" height="16" preserveAspectRatio="xMidYMid slice" clip-path="url(#${symbolId}-clip)"/></symbol>`,
      );
      byFileName[baseName] = { name: symbolId, viewBox: "0 0 16 16" };
      thumbnailCount += 1;
    }

    return {
      // Morgan extension: business-document glyphs (pdf/word/excel/email),
      // matching the resolver behind every light-DOM view. Their symbols
      // ride the injected sprite below; they self-color via the
      // --fs-file-icon-* variables, which inherit into the shadow DOM.
      byFileExtension: MORGAN_FILE_ICON_EXTENSIONS,
      byFileName,
      colored: true,
      remap: {
        "file-tree-icon-chevron": {
          name: "file-system-chevron",
          viewBox: "0 0 24 24",
        },
      },
      set: "complete" as const,
      spriteSheet: `<svg data-icon-sprite aria-hidden="true" width="0" height="0">${MORGAN_FILE_ICON_SYMBOLS}${symbols.join("")}</svg>`,
    };
  }, [currentPath, index, relativePaths]);
  // Morgan extension: the option callbacks below are captured once at model
  // creation, so they read through refs updated every commit — selection
  // and drop handling always see the latest index/manifest.
  const selectionChangeRef = React.useRef<(paths: readonly string[]) => void>(
    () => {},
  );
  const dropCompleteRef = React.useRef<(result: FileTreeDropResult) => void>(
    () => {},
  );

  const { model } = useFileTree({
    // Morgan extension: native row drag-and-drop (multi-aware, with
    // self/descendant guards) when the consumer can move items.
    dragAndDrop: dragEnabled
      ? {
          onDropComplete: (result) => dropCompleteRef.current(result),
        }
      : false,
    flattenEmptyDirectories: false,
    icons,
    initialExpansion: "closed",
    // Remounts (folder changes, manifest updates) keep the active filter.
    initialSearchQuery: searchQuery || null,
    initialSelectedPaths: initialSelectedPath ? [initialSelectedPath] : [],
    itemHeight: 28,
    overscan: 12,
    preparedInput,
    renderRowDecoration: ({ row }) => {
      const entry =
        row.kind === "file"
          ? index.files.get(`${currentPath}${row.path}`)
          : index.folders.get(normalizeFolderPath(`${currentPath}${row.path}`));

      if (!entry) return null;

      // The decoration lane renders one <span title>; CSS splits it into
      // aligned Date Modified (::before from title) and Size columns.
      const dateColumn =
        formatTimestamp(entry.updatedAt ?? entry.createdAt) ?? "—";

      if (entry.kind === "folder") {
        const childCount = index.children.get(entry.path)?.length;

        return {
          text:
            childCount === undefined
              ? "—"
              : `${childCount} ${childCount === 1 ? "item" : "items"}`,
          title: dateColumn,
        };
      }

      return { text: formatByteSize(entry.size) ?? "—", title: dateColumn };
    },
    unsafeCSS: `
      button[data-type='item']:not([data-item-selected]):hover {
        background: color-mix(in oklab, var(--color-accent) 50%, transparent);
      }
      button[data-type='item'][data-item-selected] {
        background: var(--color-primary);
        color: var(--color-primary-foreground);
        /* The primary surface is the opposite of the mode's background, so
           the row's light-dark() icon colors resolve against the opposite
           scheme — light-palette icons on the light pill in dark mode and
           vice versa. */
        color-scheme: var(--fs-selected-color-scheme, normal);
      }
      button[data-type='item'][data-item-selected] *:not([data-icon-token]):not([data-icon-token] *),
      button[data-type='item'][data-item-selected] [data-item-section]::before {
        color: var(--color-primary-foreground) !important;
      }
      [data-item-section='decoration'] > span {
        display: grid;
        grid-template-columns: 11rem 5rem;
        justify-items: end;
        white-space: nowrap;
      }
      [data-item-section='decoration'] > span::before {
        content: attr(title);
      }
      button[data-type='item'][data-item-type='folder'] [data-item-section='content'] {
        display: flex;
        align-items: center;
        min-width: 0;
      }
      button[data-type='item'][data-item-type='folder'] [data-item-section='content']::before {
        content: "";
        flex: none;
        width: 18px;
        height: 14px;
        margin-right: 4px;
        /* The brand-green glyph variants come from the light DOM (custom
           properties inherit through the shadow boundary), following the
           light/dark palette flip. */
        background: var(--fs-folder-glyph, url("${FOLDER_GLYPH_DATA_URL_LIGHT}")) center / contain no-repeat;
      }
      button[data-type='item'][data-item-selected][data-item-type='folder'] [data-item-section='content']::before {
        /* Selected rows sit on the primary surface (the brand green in
           light mode), so the glyph flips to the opposite palette to stay
           visible — the same flip the file icons get via color-scheme. */
        background: var(--fs-folder-glyph-on-primary, url("${FOLDER_GLYPH_DATA_URL_DARK}")) center / contain no-repeat;
      }
    `,
    // Morgan extension: routed through a ref so the handler reads the
    // latest index/manifest, and surfaces the tree's full multi-selection.
    onSelectionChange: (selectedPaths) =>
      selectionChangeRef.current(selectedPaths),
  });

  // Thumbnails can resolve after mount (e.g. generated client-side); push
  // sprite updates into the existing model instead of remounting the tree.
  React.useEffect(() => {
    model.setIcons(icons);
  }, [icons, model]);

  // The folders currently expanded in the mounted model, derived from the
  // given path list (the model knows the rows; the paths name the
  // directories to ask about).
  const collectExpandedDirectories = React.useCallback(
    (paths: readonly string[]) => {
      const expandedPaths: string[] = [];

      for (const directoryPath of directoryPathsOf(paths)) {
        const item =
          model.getItem(directoryPath) ?? model.getItem(`${directoryPath}/`);

        if (item && "isExpanded" in item && item.isExpanded()) {
          expandedPaths.push(directoryPath);
        }
      }
      return expandedPaths;
    },
    [model],
  );

  // Opens every given folder on the mounted model (no-ops on the already
  // open ones).
  const expandDirectories = React.useCallback(
    (directoryPaths: Iterable<string>) => {
      for (const directoryPath of directoryPaths) {
        const item =
          model.getItem(directoryPath) ?? model.getItem(`${directoryPath}/`);

        if (item && "isExpanded" in item && !item.isExpanded()) {
          item.toggle();
        }
      }
    },
    [model],
  );

  // Sort and filter changes swap the prepared input in place — remounting
  // would reset every folder's disclosure. The folders expanded in the
  // outgoing path list, the selection, and the active search query are
  // captured first and handed back to the reset.
  const appliedPreparedInputRef = React.useRef(preparedInput);
  // Filter bookkeeping: the latest prop (for unmount-time decisions), the
  // state at the last applied reset (for transition detection), and the
  // disclosure to restore once the filters clear.
  const hasActiveFiltersRef = React.useRef(hasActiveFilters);
  const filteredAtLastResetRef = React.useRef(hasActiveFilters);
  const preFilterExpansionRef = React.useRef<readonly string[] | null>(null);

  React.useEffect(() => {
    hasActiveFiltersRef.current = hasActiveFilters;
  });

  // Morgan extension: keep the model-creation-time callbacks fresh. The
  // selection handler maps the tree's relative selection onto manifest
  // entries; the drop handler reports the move upward and then reverts the
  // tree's optimistic model move — the real move lands when the consumer's
  // manifest updates (and a failed move simply leaves things as they were).
  React.useEffect(() => {
    selectionChangeRef.current = (selectedPaths) => {
      const entries: FileSystemEntry[] = [];

      for (const relativePath of selectedPaths) {
        const absolutePath = `${currentPath}${relativePath}`;
        const entry =
          index.files.get(absolutePath) ??
          index.folders.get(normalizeFolderPath(absolutePath)) ??
          null;

        if (entry) entries.push(entry);
      }
      onSelectMany(entries);
    };

    dropCompleteRef.current = (result) => {
      const targetFolderPath =
        result.target.directoryPath === null
          ? currentPath
          : normalizeFolderPath(`${currentPath}${result.target.directoryPath}`);
      const entries: FileSystemEntry[] = [];

      for (const relativePath of result.draggedPaths) {
        const absolutePath = `${currentPath}${relativePath}`;
        const entry =
          index.files.get(absolutePath) ??
          index.folders.get(normalizeFolderPath(absolutePath)) ??
          null;

        if (entry) entries.push(entry);
      }

      // Undo the tree's local move: reset back to the canonical prepared
      // input (same no-`paths` contract as the reset effect below).
      const expandedPaths = collectExpandedDirectories(
        appliedPreparedInputRef.current.paths,
      );
      const searchValue = model.getSearchValue();

      model.resetPaths(undefined as unknown as readonly string[], {
        initialExpandedPaths: expandedPaths,
        preparedInput: appliedPreparedInputRef.current,
      });
      if (searchValue) model.setSearch(searchValue);

      if (entries.length > 0) onMoveEntries(entries, targetFolderPath);
    };
  });

  React.useEffect(() => {
    const previousPreparedInput = appliedPreparedInputRef.current;

    if (previousPreparedInput === preparedInput) return;
    appliedPreparedInputRef.current = preparedInput;

    const wasFiltered = filteredAtLastResetRef.current;

    filteredAtLastResetRef.current = hasActiveFilters;

    // Filters reveal their matches the way the search session does: every
    // folder on the way to a match opens. The disclosure from just before
    // filtering is kept aside and comes back when the filters clear.
    const expandedPaths: readonly string[] = ((): readonly string[] => {
      if (hasActiveFilters) {
        if (!wasFiltered) {
          preFilterExpansionRef.current = collectExpandedDirectories(
            previousPreparedInput.paths,
          );
        }
        return [...directoryPathsOf(preparedInput.paths)];
      }
      if (wasFiltered) {
        const savedExpansion = preFilterExpansionRef.current ?? [];

        preFilterExpansionRef.current = null;
        return savedExpansion;
      }
      return collectExpandedDirectories(previousPreparedInput.paths);
    })();

    const searchValue = model.getSearchValue();

    // The `paths` argument must stay unset: when both are given, resetPaths
    // re-prepares the paths with the comparator the model was CREATED with
    // and rejects the differently-ordered prepared input. Passing only the
    // prepared input makes the reset adopt its path list as-is, and the
    // reset itself carries the selection over.
    model.resetPaths(undefined as unknown as readonly string[], {
      initialExpandedPaths: expandedPaths,
      preparedInput,
    });
    if (searchValue) model.setSearch(searchValue);
  }, [collectExpandedDirectories, hasActiveFilters, model, preparedInput]);

  // View switches and navigation unmount the tree; remember which folders
  // were left expanded and reopen them on the next mount of this folder
  // (before paint, so the restored disclosure never flashes closed). While
  // filters are active their matches are revealed instead, and the
  // remembered disclosure is the pre-filter one.
  React.useLayoutEffect(() => {
    const expansionStore = treeExpansionRef.current;
    const savedExpansion = expansionStore.get(currentPath) ?? [];

    if (hasActiveFiltersRef.current) {
      preFilterExpansionRef.current = savedExpansion;
      expandDirectories(
        directoryPathsOf(appliedPreparedInputRef.current.paths),
      );
    } else {
      expandDirectories(savedExpansion);
    }

    return () => {
      expansionStore.set(
        currentPath,
        hasActiveFiltersRef.current
          ? (preFilterExpansionRef.current ?? [])
          : collectExpandedDirectories(appliedPreparedInputRef.current.paths),
      );
    };
  }, [
    collectExpandedDirectories,
    currentPath,
    expandDirectories,
    treeExpansionRef,
  ]);

  // The toolbar search drives the tree's own search session, which filters
  // rows with hide-non-matches semantics and highlights the matched text.
  React.useEffect(() => {
    model.setSearch(searchQuery || null);
  }, [model, searchQuery]);

  // The tree's arrow keys move focus and only select on click/Enter; mirror
  // focus into the (single) selection so arrowing selects like Finder. Shift
  // ranges keep the focused row selected, so they pass through untouched.
  React.useEffect(() => {
    let lastFocusedPath = model.getFocusedPath();

    return model.subscribe(() => {
      const focusedPath = model.getFocusedPath();

      if (focusedPath === lastFocusedPath) return;

      lastFocusedPath = focusedPath;

      if (!focusedPath) return;

      const item = model.getItem(focusedPath);

      if (!item || item.isSelected()) return;

      for (const path of model.getSelectedPaths()) {
        model.getItem(path)?.deselect();
      }
      item.select();
    });
  }, [model]);

  // Rows live in the tree's shadow DOM; composedPath surfaces the row
  // element behind a pointer or keyboard event so it can resolve to a
  // manifest entry.
  const entryFromEvent = (event: React.SyntheticEvent) => {
    for (const target of event.nativeEvent.composedPath()) {
      if (!(target instanceof HTMLElement)) continue;

      const relativePath = target.dataset?.itemPath;

      if (!relativePath) continue;

      const absolutePath = `${currentPath}${relativePath}`;

      return (
        index.files.get(absolutePath) ??
        index.folders.get(normalizeFolderPath(absolutePath)) ??
        null
      );
    }
    return null;
  };

  // The tree exposes rows by relative path; directory ids may or may not
  // carry the trailing slash depending on the call site.
  const resolveTreeItem = (relativePath: string) =>
    model.getItem(relativePath) ??
    model.getItem(
      relativePath.endsWith("/")
        ? relativePath.slice(0, -1)
        : `${relativePath}/`,
    );

  // The tree's rows in display order — folders first per level, recursing
  // only into expanded folders — so type-ahead cycles exactly what's on
  // screen. Virtualization keeps this off the DOM; the index and the item
  // handles carry the same information.
  const collectVisibleEntries = () => {
    const visibleEntries: FileSystemEntry[] = [];
    const walk = (folderPath: string) => {
      const children = index.children.get(folderPath) ?? [];

      for (const child of children) {
        if (child.kind !== "folder") continue;

        const item = resolveTreeItem(child.path.slice(currentPath.length));

        if (!item) continue;
        visibleEntries.push(child);
        if ("isExpanded" in item && item.isExpanded()) walk(child.path);
      }
      for (const child of children) {
        if (child.kind === "file") visibleEntries.push(child);
      }
    };

    walk(currentPath);
    return visibleEntries;
  };

  const typeAhead = useEntryTypeAhead();

  return (
    <PierreFileTree
      model={model}
      className="block min-h-0 flex-1"
      // Finder semantics: double-clicking a folder navigates into it and
      // double-clicking a file opens it; a single click still only toggles
      // the folder's disclosure.
      onDoubleClick={(event) => {
        const entry = entryFromEvent(event);

        if (entry) onOpen(entry);
      }}
      // Morgan extension: right-clicking an unselected row selects it in
      // the tree model first (Finder behavior); the model's selection
      // change then syncs the component selection before the context menu
      // wrapper (in the root) renders its items. Inert when no item menu
      // is supplied, preserving the pristine default right-click behavior.
      onContextMenu={
        contextMenuEnabled
          ? (event) => {
              const entry = entryFromEvent(event);

              if (!entry) return;

              const relativePath = entry.path.slice(currentPath.length);
              const item = resolveTreeItem(
                relativePath.endsWith("/")
                  ? relativePath.slice(0, -1)
                  : relativePath,
              );

              if (!item || item.isSelected()) return;
              for (const path of model.getSelectedPaths()) {
                model.getItem(path)?.deselect();
              }
              item.select();
            }
          : undefined
      }
      // Morgan extension: OS file drops onto folder rows. Internal tree
      // drags are handled natively by the tree (its own handlers ignore
      // drags without a session, so these only ever see external drags).
      onDragOver={(event) => {
        if (!acceptsExternalDrop) return;
        if (!event.dataTransfer.types.includes("Files")) return;

        const entry = entryFromEvent(event);

        if (entry?.kind === "folder") event.preventDefault();
      }}
      onDrop={(event) => {
        if (!acceptsExternalDrop) return;
        if (!event.dataTransfer.types.includes("Files")) return;

        const entry = entryFromEvent(event);

        if (entry?.kind !== "folder") return;
        event.preventDefault();
        event.stopPropagation();
        onExternalFolderDrop(entry.path, event.dataTransfer);
      }}
      // Enter mirrors the other views: navigate into the focused folder or
      // open the focused file. Printable keys run the shared type-ahead
      // over the visible rows.
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          const entry = entryFromEvent(event);

          if (entry) {
            event.preventDefault();
            onOpen(entry);
          }
          return;
        }

        if (!isTypeAheadKey(event)) return;

        const visibleEntries = collectVisibleEntries();
        const focusedPath = model.getFocusedPath()?.replace(/\/$/, "") ?? null;
        const focusedIndex = visibleEntries.findIndex(
          (entry) =>
            entry.path.slice(currentPath.length).replace(/\/$/, "") ===
            focusedPath,
        );
        const match = typeAhead(event, visibleEntries, focusedIndex);

        if (!match) return;

        const item = resolveTreeItem(match.path.slice(currentPath.length));

        if (item) {
          model.scrollToPath(item.getPath());
          item.focus();
        }
      }}
      style={
        {
          "--trees-bg-override": "transparent",
          "--trees-border-color-override": "var(--color-border)",
          "--trees-fg-override": "var(--color-foreground)",
          // Match the focus-visible ring used by the tabs and the other
          // views (`ring-2 ring-ring`) instead of the tree's accent blue.
          "--trees-focus-ring-color-override": "var(--color-ring)",
          "--trees-focus-ring-width-override": "2px",
          "--trees-selected-bg-override": "var(--color-primary)",
          "--trees-selected-focused-border-color-override": "var(--color-ring)",
        } as React.CSSProperties
      }
    />
  );
}

function FileSystemColumnsView(props: FileSystemViewProps) {
  const {
    acceptsExternalDrop,
    canDragItems,
    currentPath,
    index,
    loadPreviewImageUrl,
    loadingFolders,
    onFolderDrop,
    onItemDragStart,
    onOpen,
    onRangeSelect,
    onSelect,
    onToggleSelect,
    pageUrlCache,
    renderFilePreview,
    renderPreviewDetails,
    renderLargePreview,
    selectedEntry,
    selectedPath,
    selectedPathsSet,
  } = props;
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
  const rowRefs = React.useRef(new Map<string, HTMLButtonElement>());

  // Shared, drag-resizable width for every file-list column. Tracked in a ref
  // (read synchronously during the pointer drag) and mirrored to a CSS
  // variable on the column-row container so resizing updates the DOM directly
  // without re-rendering the memoized columns.
  const columnWidthRef = React.useRef(COLUMN_DEFAULT_WIDTH);
  const [columnWidth, setColumnWidth] = React.useState(COLUMN_DEFAULT_WIDTH);
  React.useEffect(() => {
    const stored = readStoredColumnWidth();
    columnWidthRef.current = stored;
    setColumnWidth(stored);
  }, []);
  const handleColumnResizeStart = React.useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      const startX = event.clientX;
      const startWidth = columnWidthRef.current;

      const handlePointerMove = (moveEvent: PointerEvent) => {
        const next = clampColumnWidth(
          startWidth + (moveEvent.clientX - startX),
        );
        columnWidthRef.current = next;
        setColumnWidth(next);
      };
      const handlePointerUp = () => {
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
        document.body.style.removeProperty("cursor");
        document.body.style.removeProperty("user-select");
        window.localStorage.setItem(
          COLUMN_WIDTH_STORAGE_KEY,
          String(columnWidthRef.current),
        );
      };

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
    },
    [],
  );

  // The selection highlight tracks every keypress; mounting the trailing
  // child column and the preview pane is deferred so holding an arrow key
  // doesn't pay that DOM churn per step.
  const deferredSelectedEntry = React.useDeferredValue(selectedEntry);
  const deferredSelectedPath = React.useDeferredValue(selectedPath);
  const pendingFocusPathRef = React.useRef<string | null>(null);
  const typeAhead = useEntryTypeAhead();

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!ARROW_KEYS.has(event.key)) {
      // Type-ahead moves within the active column's rows, like Finder.
      const siblings =
        selectedEntry && selectedPath?.startsWith(currentPath)
          ? (index.children.get(selectedEntry.parentPath) ?? [])
          : (index.children.get(currentPath) ?? []);
      const match = typeAhead(
        event,
        siblings,
        siblings.findIndex((sibling) => sibling.path === selectedPath),
      );

      if (match) {
        onSelect(match);

        const row = rowRefs.current.get(match.path);

        if (row) {
          row.focus();
        } else {
          pendingFocusPathRef.current = match.path;
        }
      }
      return;
    }

    const nextEntry: FileSystemEntry | null | undefined = (():
      FileSystemEntry | null | undefined => {
      if (!selectedEntry || !selectedPath?.startsWith(currentPath)) {
        return index.children.get(currentPath)?.[0];
      }
      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        const siblings = index.children.get(selectedEntry.parentPath) ?? [];
        const currentIndex = siblings.findIndex(
          (sibling) => sibling.path === selectedEntry.path,
        );

        return siblings[currentIndex + (event.key === "ArrowUp" ? -1 : 1)];
      }
      if (event.key === "ArrowLeft") {
        return selectedEntry.parentPath !== currentPath
          ? index.folders.get(selectedEntry.parentPath)
          : undefined;
      }
      return selectedEntry.kind === "folder"
        ? index.children.get(selectedEntry.path)?.[0]
        : undefined;
    })();

    if (!nextEntry) return;

    onSelect(nextEntry);

    const row = rowRefs.current.get(nextEntry.path);

    if (row) {
      pendingFocusPathRef.current = null;
      row.focus();
    } else {
      // The target row lives in a deferred column that hasn't mounted yet;
      // focus it from the effect below once it exists.
      pendingFocusPathRef.current = nextEntry.path;
    }
    event.preventDefault();
  };

  React.useEffect(() => {
    const path = pendingFocusPathRef.current;

    if (!path) return;

    const row = rowRefs.current.get(path);

    if (row) {
      pendingFocusPathRef.current = null;
      row.focus();
    }
  });

  const columnPaths = React.useMemo(() => {
    const paths = [currentPath];

    if (!deferredSelectedPath?.startsWith(currentPath)) return paths;

    const targetFolder =
      deferredSelectedEntry?.kind === "folder"
        ? deferredSelectedEntry.path
        : (deferredSelectedEntry?.parentPath ?? currentPath);
    const relativePath = targetFolder.slice(currentPath.length);

    for (const segment of relativePath.split("/")) {
      if (!segment) continue;
      paths.push(`${paths[paths.length - 1]}${segment}/`);
    }
    return paths;
  }, [currentPath, deferredSelectedEntry, deferredSelectedPath]);
  // Roving tabindex: all columns together form a single tab stop (the
  // selected row when its column is mounted, else the first row), so
  // Shift+Tab returns to the toolbar like in the list view.
  const tabStopPath = React.useMemo(() => {
    if (selectedPath) {
      for (const columnPath of columnPaths) {
        if (
          index.children
            .get(columnPath)
            ?.some((entry) => entry.path === selectedPath)
        ) {
          return selectedPath;
        }
      }
    }
    return index.children.get(columnPaths[0] ?? "")?.[0]?.path ?? null;
  }, [columnPaths, index, selectedPath]);
  // Morgan extension: the columns are memoized on scalar selection props so
  // arrow-key navigation only re-renders the columns whose rows change. A
  // plain select collapses `selectedPathsSet` to the primary path (already
  // covered by `selectedChildPath`), so a stable empty set is passed while
  // the multi-selection is trivial — only real multi-select operations
  // re-render every column.
  const multiSelectedPaths =
    selectedPathsSet.size > 1 ? selectedPathsSet : EMPTY_PATH_SET;
  const selectedFile =
    deferredSelectedEntry?.kind === "file"
      ? (deferredSelectedEntry as FileEntry)
      : null;
  const selectedFileSize = selectedFile
    ? formatByteSize(selectedFile.size)
    : null;
  // A live document preview (Word/Excel/text) for the selected file, when
  // the consumer supplies one; it replaces the type-icon thumbnail.
  const selectedLargePreview = selectedFile
    ? renderLargePreview?.(selectedFile)
    : null;

  React.useEffect(() => {
    const container = scrollContainerRef.current;

    if (container) container.scrollLeft = container.scrollWidth;
  }, [columnPaths.length, deferredSelectedPath]);

  return (
    <ScrollArea
      orientation="horizontal"
      viewportRef={scrollContainerRef}
      viewportClassName="overscroll-x-contain"
    >
      {/* The Content part's ResizeObserver tells the scroll area when the
          trail shrinks (deselect, shallower selection) so the horizontal
          scrollbar hides; the viewport alone only observes its own box. Its
          built-in inline min-width (fit-content) would beat a min-w-full
          class, so the full-width floor is inline too. */}
      <ScrollAreaPrimitive.Content
        className="flex h-full w-max"
        style={
          {
            minWidth: "100%",
            "--fs-column-width": `${columnWidth}px`,
          } as React.CSSProperties
        }
        onKeyDown={handleKeyDown}
      >
        {columnPaths.map((columnPath, columnIndex) => (
          <FileSystemColumn
            key={columnPath || "(root)"}
            // Morgan extension: drag/drop + multi-select wiring (the
            // callbacks are stable useCallbacks, memo-safe).
            acceptsExternalDrop={acceptsExternalDrop}
            canDragItems={canDragItems}
            entries={index.children.get(columnPath) ?? []}
            index={index}
            isLoading={loadingFolders.has(columnPath)}
            multiSelectedPaths={multiSelectedPaths}
            onFolderDrop={onFolderDrop}
            onItemDragStart={onItemDragStart}
            onColumnResizeStart={handleColumnResizeStart}
            onOpen={onOpen}
            onRangeSelect={onRangeSelect}
            onSelect={onSelect}
            onToggleSelect={onToggleSelect}
            rowRefs={rowRefs}
            // Scalar per-column props so the memoized column only
            // re-renders when its own rows change — a selection deeper in
            // the trail leaves ancestor columns untouched.
            selectedChildPath={
              selectedPath && pathParent(selectedPath) === columnPath
                ? selectedPath
                : null
            }
            tabStopChildPath={
              tabStopPath && pathParent(tabStopPath) === columnPath
                ? tabStopPath
                : null
            }
            trailChildPath={columnPaths[columnIndex + 1] ?? null}
          />
        ))}
        {selectedFile ? (
          <ScrollArea
            orientation="vertical"
            className="min-w-72 flex-1"
            viewportClassName="flex justify-center p-4"
          >
            <div className="flex w-full max-w-2xl flex-col items-stretch gap-3">
              {selectedLargePreview ? (
                // Live document preview (Word/Excel/text) fills the pane with
                // its own internal scroll, capped so the metadata below stays
                // reachable.
                <div className="h-[28rem] max-h-[60vh] min-h-[16rem] w-full shrink-0 overflow-hidden rounded-lg border">
                  {selectedLargePreview}
                </div>
              ) : (
                // Width derives from the aspect ratio so the thumbnail grows
                // with the pane up to a 32rem height cap.
                <div
                  className="mx-auto w-full shrink-0"
                  style={{
                    maxWidth: `min(100%, ${(selectedFile.previewAspectRatio ?? 0.78) * 32}rem)`,
                  }}
                >
                  <FileVisual
                    file={selectedFile}
                    className="w-full"
                    loadPreviewImageUrl={loadPreviewImageUrl}
                    pageable
                    pageUrlCache={pageUrlCache}
                    previewAspectRatio={0.78}
                    renderFilePreview={renderFilePreview}
                  />
                </div>
              )}
              <div className="text-center">
                <div className="break-words text-sm font-semibold">
                  {selectedFile.name}
                </div>
                <div className="text-xs text-muted-foreground">
                  {fileKindLabel(selectedFile)}
                  {selectedFileSize ? ` - ${selectedFileSize}` : null}
                </div>
              </div>
              {renderPreviewDetails?.(selectedFile)}
              <FileSystemInformation entry={selectedFile} index={index} />
            </div>
          </ScrollArea>
        ) : null}
      </ScrollAreaPrimitive.Content>
    </ScrollArea>
  );
}

// Column row geometry (px at the default 16px root font size).
const COLUMN_PADDING = 6; // p-1.5
const COLUMN_ROW_HEIGHT = 28; // h-7
const COLUMN_ROW_GAP = 1; // gap-px
const COLUMN_ROW_STRIDE = COLUMN_ROW_HEIGHT + COLUMN_ROW_GAP;

// Drag-to-resize bounds for the column-view file lists. All columns share one
// width (Finder-style); the value persists across sessions so a practitioner's
// preferred width sticks once they've widened it to read long file names.
const COLUMN_MIN_WIDTH = 180;
const COLUMN_MAX_WIDTH = 640;
const COLUMN_DEFAULT_WIDTH = 240; // w-60
const COLUMN_WIDTH_STORAGE_KEY = "morgan.fs.columnWidth";

/** Clamp a candidate column width into the supported range. */
function clampColumnWidth(width: number): number {
  return Math.min(COLUMN_MAX_WIDTH, Math.max(COLUMN_MIN_WIDTH, width));
}

/** Read the persisted column width, falling back to the default. */
function readStoredColumnWidth(): number {
  if (typeof window === "undefined") return COLUMN_DEFAULT_WIDTH;
  const raw = window.localStorage.getItem(COLUMN_WIDTH_STORAGE_KEY);
  const parsed = raw ? Number.parseInt(raw, 10) : Number.NaN;
  return Number.isFinite(parsed)
    ? clampColumnWidth(parsed)
    : COLUMN_DEFAULT_WIDTH;
}

// Memoized with scalar selection props: pressing into a deep trail only
// re-renders the columns whose rows actually change.
const FileSystemColumn = React.memo(function FileSystemColumn({
  acceptsExternalDrop,
  canDragItems,
  entries,
  index,
  isLoading,
  multiSelectedPaths,
  onColumnResizeStart,
  onFolderDrop,
  onItemDragStart,
  onOpen,
  onRangeSelect,
  onSelect,
  onToggleSelect,
  rowRefs,
  selectedChildPath,
  tabStopChildPath,
  trailChildPath,
}: {
  acceptsExternalDrop: boolean;
  canDragItems: boolean;
  entries: FileSystemEntry[];
  index: FileSystemIndex;
  isLoading: boolean;
  multiSelectedPaths: ReadonlySet<string>;
  onColumnResizeStart: (event: React.PointerEvent<HTMLElement>) => void;
  onFolderDrop: (folderPath: string, dataTransfer: DataTransfer) => void;
  onItemDragStart: (entry: FileSystemEntry, dataTransfer: DataTransfer) => void;
  onOpen: (entry: FileSystemEntry) => void;
  onRangeSelect: (
    entry: FileSystemEntry,
    orderedEntries: readonly FileSystemEntry[],
  ) => void;
  onSelect: (entry: FileSystemEntry | null) => void;
  onToggleSelect: (entry: FileSystemEntry) => void;
  rowRefs: React.MutableRefObject<Map<string, HTMLButtonElement>>;
  selectedChildPath: string | null;
  tabStopChildPath: string | null;
  trailChildPath: string | null;
}) {
  const viewportRef = React.useRef<HTMLDivElement | null>(null);
  // Morgan extension: folder row currently highlighted as a drop target.
  const [dragOverPath, setDragOverPath] = React.useState<string | null>(null);
  const acceptsDropOn = (entry: FileSystemEntry, dataTransfer: DataTransfer) =>
    entry.kind === "folder" &&
    (dataTransfer.types.includes(MORGAN_ITEMS_DRAG_TYPE)
      ? canDragItems
      : acceptsExternalDrop && dataTransfer.types.includes("Files"));
  const { end, start } = useVirtualWindow({
    count: entries.length,
    itemStride: COLUMN_ROW_STRIDE,
    leadingPx: COLUMN_PADDING,
    overscan: 10,
    viewportRef,
  });

  // Keyboard navigation can select a row this column hasn't mounted; scroll
  // it into the viewport so it mounts and the pending-focus effect can land.
  React.useLayoutEffect(() => {
    if (!selectedChildPath) return;

    scrollIndexIntoView({
      index: entries.findIndex((entry) => entry.path === selectedChildPath),
      itemSize: COLUMN_ROW_HEIGHT,
      itemStride: COLUMN_ROW_STRIDE,
      leadingPx: COLUMN_PADDING,
      viewport: viewportRef.current,
    });
  }, [entries, selectedChildPath]);

  return (
    <div className="relative flex w-[var(--fs-column-width,15rem)] shrink-0">
      <ScrollArea
        orientation="vertical"
        className="size-full border-r"
        viewportRef={viewportRef}
        viewportClassName="p-1.5"
        viewportProps={{ "aria-label": "Files", role: "listbox" }}
      >
        {isLoading && entries.length === 0 ? (
          <div className="animate-pulse px-2 py-1.5 text-xs text-muted-foreground motion-reduce:animate-none">
            Loading…
          </div>
        ) : (
          <div
            className="relative"
            style={{
              height: entries.length
                ? entries.length * COLUMN_ROW_STRIDE - COLUMN_ROW_GAP
                : undefined,
            }}
          >
            <div
              className="absolute inset-x-0 flex flex-col gap-px"
              style={{ top: start * COLUMN_ROW_STRIDE }}
            >
              {entries.slice(start, end).map((entry) => {
                // Morgan extension: the multi-selection highlights alongside
                // the primary (same selected styling).
                const isSelected =
                  entry.path === selectedChildPath ||
                  multiSelectedPaths.has(entry.path);
                const isDropTarget = entry.path === dragOverPath;
                const isOnTrail =
                  entry.kind === "folder" && entry.path === trailChildPath;

                const coverUrl =
                  entry.kind === "file" ? filePreviewUrls(entry)[0] : undefined;

                return (
                  <button
                    key={entry.path}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    // Selected rows sit on the primary surface — the opposite
                    // of the mode's background — so the file-type icon swaps
                    // to the opposite palette.
                    data-file-system-on-primary={isSelected ? "" : undefined}
                    // Morgan extension: context-menu target resolution.
                    data-file-system-path={entry.path}
                    tabIndex={entry.path === tabStopChildPath ? 0 : -1}
                    ref={(element) => {
                      if (element) {
                        rowRefs.current.set(entry.path, element);
                      } else {
                        rowRefs.current.delete(entry.path);
                      }
                    }}
                    // Morgan extension: drag source + folder drop target
                    // (same wiring as the icons/extended-list rows).
                    draggable={canDragItems || undefined}
                    onDragStart={
                      canDragItems
                        ? (event) => onItemDragStart(entry, event.dataTransfer)
                        : undefined
                    }
                    onDragOver={(event) => {
                      if (!acceptsDropOn(entry, event.dataTransfer)) return;
                      event.preventDefault();
                      setDragOverPath(entry.path);
                    }}
                    onDragLeave={() => {
                      setDragOverPath((previous) =>
                        previous === entry.path ? null : previous,
                      );
                    }}
                    onDrop={(event) => {
                      if (!acceptsDropOn(entry, event.dataTransfer)) return;
                      event.preventDefault();
                      event.stopPropagation();
                      setDragOverPath(null);
                      onFolderDrop(entry.path, event.dataTransfer);
                    }}
                    // Selecting on press (mouse only) starts mounting the
                    // child column a beat before mouseup — the immediacy
                    // @pierre/trees rows have. Touch keeps selection on the
                    // click so scroll gestures don't select. Morgan
                    // extension: modifier presses defer to the click handler
                    // (toggle/range must see the pre-press selection), and a
                    // press on an already multi-selected row defers too so a
                    // drag carries the whole selection while a plain click
                    // still collapses it on mouseup.
                    onPointerDown={(event) => {
                      if (
                        event.metaKey ||
                        event.ctrlKey ||
                        event.shiftKey ||
                        multiSelectedPaths.has(entry.path)
                      ) {
                        return;
                      }
                      if (event.pointerType === "mouse" && event.button === 0) {
                        onSelect(entry);
                      }
                    }}
                    onClick={(event) => {
                      // Morgan extension: cmd/ctrl toggles, shift extends
                      // within this column (cross-column ranges don't exist
                      // in Miller columns; a foreign anchor falls back to a
                      // plain select, like Finder).
                      if (event.metaKey || event.ctrlKey) {
                        onToggleSelect(entry);
                      } else if (event.shiftKey) {
                        onRangeSelect(entry, entries);
                      } else {
                        onSelect(entry);
                      }
                    }}
                    onDoubleClick={() => onOpen(entry)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") onOpen(entry);
                    }}
                    className={cn(
                      "flex h-7 shrink-0 items-center gap-2 rounded-md px-2 py-1 text-left text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      isSelected
                        ? "bg-primary text-primary-foreground"
                        : isOnTrail
                          ? "bg-accent"
                          : "hover:bg-accent/50",
                      isDropTarget && "bg-accent ring-2 ring-ring",
                    )}
                  >
                    {entry.kind === "folder" ? (
                      <FileSystemFolderGlyph className="h-3.5 w-auto shrink-0" />
                    ) : coverUrl ? (
                      <img
                        src={coverUrl}
                        alt=""
                        draggable={false}
                        className="size-4 shrink-0 rounded-[3px] bg-white object-cover"
                      />
                    ) : (
                      <FileTypeIcon
                        fileName={entry.name}
                        className="size-4 shrink-0"
                      />
                    )}
                    <span
                      className="min-w-0 flex-1 truncate"
                      title={entry.name}
                    >
                      {entry.name}
                    </span>
                    {entry.kind === "folder" &&
                    folderHasChildren(index, entry) ? (
                      <HugeiconsIcon
                        icon={ArrowRight01Icon}
                        className={cn(
                          "size-3.5 shrink-0",
                          !isSelected && "text-muted-foreground/60",
                        )}
                      />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </ScrollArea>
      {/* Drag handle on the right edge: resizes all columns together (shared
          width via the parent's --fs-column-width variable). */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize columns"
        onPointerDown={onColumnResizeStart}
        className="group/resize absolute inset-y-0 right-0 z-10 flex w-2 translate-x-1/2 cursor-col-resize touch-none items-stretch justify-center"
      >
        <span className="w-px bg-transparent transition-colors group-hover/resize:bg-primary" />
      </div>
    </div>
  );
});

function FileSystemInformation({
  entry,
  index,
}: {
  entry: FileSystemEntry;
  index: FileSystemIndex;
}) {
  const rows: Array<[string, string]> = [];
  const created = formatTimestamp(entry.createdAt);
  const updated = formatTimestamp(entry.updatedAt);

  if (created) rows.push(["Created", created]);
  if (updated) rows.push(["Modified", updated]);
  if (entry.kind === "file") {
    const size = formatByteSize(entry.size);

    if (size) rows.push(["Size", size]);
  } else {
    const childCount = index.children.get(entry.path)?.length;

    if (childCount !== undefined) {
      rows.push(["Items", `${childCount}`]);
    }
  }

  if (rows.length === 0) return null;

  return (
    <div className="border-t pt-3">
      <div className="mb-1.5 text-xs font-semibold">Information</div>
      <dl className="space-y-1">
        {rows.map(([label, value]) => (
          <div
            key={label}
            className="flex items-baseline justify-between gap-3 text-xs"
          >
            <dt className="shrink-0 text-muted-foreground">{label}</dt>
            <dd className="text-right" suppressHydrationWarning>
              {value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// Filmstrip geometry (px at the default 16px root font size).
const GALLERY_STRIP_PADDING = 8; // p-2
const GALLERY_TILE_SIZE = 56; // size-14
const GALLERY_TILE_GAP = 6; // gap-1.5
const GALLERY_TILE_STRIDE = GALLERY_TILE_SIZE + GALLERY_TILE_GAP;
// How many visited stages stay mounted so stepping back to a recent file
// restores its already-loaded preview without refetching or re-parsing;
// also bounds the memory the keep-alive pool can hold onto.
const GALLERY_STAGE_POOL_SIZE = 4;
// Of those, how many stay attached to the DOM (the active stage plus the
// two before it, keeping the usual two-or-three-file rotation instant).
// The rest wait detached, costing no layout or style-recalc work until
// they return — their page canvases remount on the way back, so returning
// to a detached stage briefly rebuilds the page content.
const GALLERY_STAGE_ATTACHED_COUNT = 3;

// The preview for one pooled file. Each stage owns its URL resolution and
// viewer state, so a mounted stage is self-contained: the root keeps
// recently shown stages alive (reparented between hosts rather than
// remounted, because the document viewers load in effects and would
// refetch and re-parse on remount) and revisiting one — in the gallery or
// the dialog — skips the presign, download, and parse work instead of
// re-running it behind a spinner. The two variants share one element
// structure, differing only in props and classes, so flipping a mounted
// stage between them keeps the viewer instance.
function FileSystemGalleryStage({
  file,
  getFileUrl,
  loadPreviewImageUrl,
  pageUrlCache,
  renderFilePreview,
  renderLargePreview,
  urlCache,
}: {
  file: FileEntry;
  getFileUrl?: (file: FileSystemFileItem) => string | Promise<string>;
  loadPreviewImageUrl?: (
    file: FileSystemFileItem,
    pageIndex: number,
  ) => Promise<string | null>;
  pageUrlCache?: Map<string, string>;
  renderFilePreview?: (file: FileSystemFileItem) => React.ReactNode;
  renderLargePreview?: (file: FileSystemFileItem) => React.ReactNode;
  urlCache: Map<string, string>;
}) {
  // A live document preview (Word/Excel/text) takes the whole stage with its
  // own internal scroll, in place of the type-icon thumbnail. This is just a
  // React element (createElement) — its own hooks run when it renders as a
  // child below, so computing it before the hooks here is safe.
  const largePreview = renderLargePreview?.(file);
  const viewerKind = viewerKindForFile(file);
  // Only viewer-backed stages need a URL; thumbnail stages render from the
  // manifest's preview images, so selecting them never triggers a presign.
  const { isResolving, url } = useResolvedFileUrl(
    viewerKind ? file : null,
    getFileUrl,
    urlCache,
  );

  if (largePreview) {
    return (
      <div className="size-full overflow-hidden rounded-lg border bg-background">
        {largePreview}
      </div>
    );
  }
  if (viewerKind && isResolving) {
    return <Spinner className="size-6 text-muted-foreground" />;
  }
  if (viewerKind === "image" && url) {
    return (
      <img
        src={url}
        alt={file.name}
        className="max-h-full max-w-full rounded-lg object-contain"
      />
    );
  }
  return (
    <FileVisual
      file={file}
      className="w-80 max-w-full"
      loadPreviewImageUrl={loadPreviewImageUrl}
      pageable
      pageUrlCache={pageUrlCache}
      previewAspectRatio={0.78}
      renderFilePreview={renderFilePreview}
    />
  );
}

function FileSystemGalleryView(props: FileSystemViewProps) {
  const {
    attachedStagePaths,
    entries,
    index,
    onOpen,
    onSelect,
    poolStagePath,
    registerStageHost,
    renderFilePreview,
    renderPreviewDetails,
    selectedEntry,
    selectedPath,
  } = props;
  const stripRefs = React.useRef(new Map<string, HTMLButtonElement>());
  const stripViewportRef = React.useRef<HTMLDivElement | null>(null);
  const typeAhead = useEntryTypeAhead();
  const activeEntry =
    selectedEntry && entries.some((entry) => entry.path === selectedEntry.path)
      ? selectedEntry
      : (entries[0] ?? null);
  const activeFile = activeEntry?.kind === "file" ? activeEntry : null;
  // While arrow keys are scrubbing the strip, the center pane shows a
  // spinner; a file is only admitted to the preview pool (mounting its
  // viewer and resolving its URL) once the selection settles so each
  // keystroke stays cheap.
  const settledPath = useSettledValue(activeEntry?.path ?? null, 200);

  React.useEffect(() => {
    if (settledPath) poolStagePath(settledPath);
  }, [poolStagePath, settledPath]);

  // Hosts for the root-owned preview pool: one positioned wrapper per
  // pooled path; the root reparents each live preview into its wrapper.
  // Stable callbacks per path keep React from re-running the host refs on
  // unrelated renders.
  const stageHostRefs = React.useMemo(
    () =>
      new Map(
        attachedStagePaths.map(
          (path) =>
            [
              path,
              (element: HTMLElement | null) => registerStageHost(path, element),
            ] as const,
        ),
      ),
    [attachedStagePaths, registerStageHost],
  );

  const activeFileSize = activeFile ? formatByteSize(activeFile.size) : null;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (entries.length === 0) return;

    const currentIndex = activeEntry
      ? entries.findIndex((entry) => entry.path === activeEntry.path)
      : -1;

    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
      const match = typeAhead(event, entries, currentIndex);

      if (match) {
        onSelect(match);
        // The matched tile may be outside the strip's virtual window; the
        // active-tile effect scrolls it in, and focus follows once mounted.
        requestAnimationFrame(() => stripRefs.current.get(match.path)?.focus());
      }
      return;
    }

    const nextEntry =
      entries[
        currentIndex === -1
          ? 0
          : currentIndex + (event.key === "ArrowLeft" ? -1 : 1)
      ];

    if (!nextEntry) return;

    onSelect(nextEntry);
    stripRefs.current.get(nextEntry.path)?.focus();
    event.preventDefault();
  };

  const { end: stripEnd, start: stripStart } = useVirtualWindow({
    count: entries.length,
    horizontal: true,
    itemStride: GALLERY_TILE_STRIDE,
    leadingPx: GALLERY_STRIP_PADDING,
    overscan: 8,
    viewportRef: stripViewportRef,
  });

  // Keep the active tile mounted and visible while scrubbing or when the
  // selection arrives from another view.
  const activePath = activeEntry?.path ?? null;

  React.useLayoutEffect(() => {
    if (!activePath) return;

    scrollIndexIntoView({
      horizontal: true,
      index: entries.findIndex((entry) => entry.path === activePath),
      itemSize: GALLERY_TILE_SIZE,
      itemStride: GALLERY_TILE_STRIDE,
      leadingPx: GALLERY_STRIP_PADDING,
      viewport: stripViewportRef.current,
    });
  }, [activePath, entries]);

  return (
    <div className="flex size-full flex-col" onKeyDown={handleKeyDown}>
      {/* The strip comes first in DOM order (rendered below via order-last)
          so the filmstrip is the view's single tab stop: Shift+Tab exits to
          the toolbar instead of landing inside the embedded viewers. */}
      <ScrollArea
        orientation="horizontal"
        className="order-last h-auto w-full shrink-0 border-t"
        viewportRef={stripViewportRef}
        viewportClassName="p-2"
      >
        <div
          className="relative h-14 min-w-full"
          style={{
            width: entries.length
              ? entries.length * GALLERY_TILE_STRIDE - GALLERY_TILE_GAP
              : undefined,
          }}
        >
          <div
            role="listbox"
            aria-label="Files"
            className="absolute inset-y-0 flex items-center gap-1.5"
            style={{ left: stripStart * GALLERY_TILE_STRIDE }}
          >
            {entries.slice(stripStart, stripEnd).map((entry) => {
              const isActive =
                entry.path === (activeEntry?.path ?? selectedPath);

              return (
                <button
                  key={entry.path}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  // Morgan extension: context-menu target resolution.
                  data-file-system-path={entry.path}
                  tabIndex={isActive ? 0 : -1}
                  ref={(element) => {
                    if (element) {
                      stripRefs.current.set(entry.path, element);
                    } else {
                      stripRefs.current.delete(entry.path);
                    }
                  }}
                  onClick={() => onSelect(entry)}
                  onDoubleClick={() => onOpen(entry)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") onOpen(entry);
                  }}
                  title={entry.name}
                  className={cn(
                    "flex size-14 shrink-0 items-center justify-center rounded-md border border-transparent p-1 outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    isActive && "border-ring/40 bg-accent",
                  )}
                >
                  {entry.kind === "folder" ? (
                    <FileSystemFolderGlyph className="h-9 w-auto" />
                  ) : (
                    <FileVisual
                      file={entry}
                      className="w-9 rounded-sm"
                      previewAspectRatio={0.78}
                      renderFilePreview={renderFilePreview}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </ScrollArea>
      <div className="flex min-h-0 flex-1">
        <div className="relative flex min-h-0 min-w-0 flex-1 items-center justify-center p-3">
          {activeEntry?.kind === "folder" ? (
            <FileSystemFolderGlyph className="h-40 max-h-full w-auto drop-shadow-md" />
          ) : activeFile && !attachedStagePaths.includes(activeFile.path) ? (
            <Spinner className="size-6 text-muted-foreground" />
          ) : null}
          {/* Inactive hosts hide via `visibility` + `opacity`, never
              `display`: the document viewers size pages off ResizeObserver
              measurements, and display:none would collapse them to zero
              width — every reveal would re-lay-out and re-rasterize behind
              a blank pane. Stacking absolutely keeps each hidden stage at
              its real size so revealing one is pure paint. `opacity-0`
              matters: descendants can override an inherited
              visibility:hidden with their own visibility:visible (the
              spreadsheet grid's cell-selection overlay does), but nothing
              can opt out of an ancestor's zero opacity. `inert` keeps the
              hidden viewer's focusables out of reach. */}
          {attachedStagePaths.map((path) => {
            const isActiveStage = path === activeFile?.path;

            return (
              <div
                key={path}
                ref={stageHostRefs.get(path)}
                {...({
                  inert: !isActiveStage ? "" : undefined,
                } as unknown as React.HTMLAttributes<HTMLDivElement>)}
                className={cn(
                  "absolute inset-0 flex items-center justify-center p-3",
                  !isActiveStage && "invisible opacity-0",
                )}
              />
            );
          })}
        </div>
        {activeEntry ? (
          <ScrollArea
            orientation="vertical"
            className="hidden w-72 shrink-0 border-l sm:block"
            viewportClassName="flex flex-col gap-3 p-4"
          >
            <div className="flex items-center gap-3">
              {activeFile ? (
                <FileVisual
                  file={activeFile}
                  className={cn(
                    "shrink-0 rounded-sm",
                    (activeFile.previewAspectRatio ?? 0.78) > 1.2
                      ? "w-16"
                      : "w-9",
                  )}
                  previewAspectRatio={0.78}
                  renderFilePreview={renderFilePreview}
                />
              ) : (
                <FileSystemFolderGlyph className="h-8 w-auto shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="break-words text-sm font-semibold">
                  {activeEntry.name}
                </div>
                <div className="text-xs text-muted-foreground">
                  {activeFile ? fileKindLabel(activeFile) : "Folder"}
                  {activeFileSize ? ` - ${activeFileSize}` : null}
                </div>
              </div>
            </div>
            {activeFile ? renderPreviewDetails?.(activeFile) : null}
            <FileSystemInformation entry={activeEntry} index={index} />
          </ScrollArea>
        ) : null}
      </div>
    </div>
  );
}
